import {
  getDb,
  doc,
  arrayUnion,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
  addDoc,
  limit,
  orderBy,
  onSnapshot,
  Unsubscribe,
  writeBatch,
  isFirebaseInitialized,
  type User,
  unifiedGetDoc,
  unifiedSetDoc,
  unifiedUpdateDoc,
  unifiedDeleteDoc,
  getStandardizedMovieId,
  unifiedAddDoc
} from '@watchlist/shared';
import { TMDbMovie } from './tmdbApi';
import { TMDB_IMAGE_BASE_URL } from '@watchlist/shared';
import tmdbRequestQueue from './tmdbRequestQueue';
import { migrateMovieId } from '../utils/movieUtils';
import { getCachedUserProfile } from '../utils/profileCache';
import { userSearchCache } from '../utils/userSearchCache';
import {
  UserWatchlist,
  UserProfile,
  UserPreferences,
  MovieState,
  authService,
  watchlistService,
  socialService,
  movieService,
  dataService,
  unifiedCollection,
  TEST_ACCOUNT_EMAILS,
  USER_ROLES,
  getWaitlistEnabled,
  UserRole,
  accessControlService,
} from '@watchlist/shared';
import { nativeFirebase } from './nativeFirebaseService';
import { isCapacitorNative } from '../utils/platformUtils';
import { parseFirebaseDate, formatFirebaseDate } from '../utils/dateUtils';


// Database instance is now accessed via getDb() from shared firebaseBase
// On native platforms, we use nativeFirebase for better performance

/** Firestore rejects `undefined` in maps (e.g. metadata.moviePoster when optional). */
function notificationMetadataFrom(entries: Record<string, unknown>): Record<string, unknown> | undefined {
  const o = Object.fromEntries(Object.entries(entries).filter(([, v]) => v !== undefined));
  return Object.keys(o).length > 0 ? o : undefined;
}

// Movie interface imported from shared package

// Firestore query limits for cost optimization and performance
const QUERY_LIMITS = {
  USER_MOVIES: 1000,           // Safety limit for power users with large collections
  WATCHLISTS_USER: 100,        // Typical user has < 20 watchlists
  WATCHLISTS_PUBLIC: 50,       // Initial discovery feed
  NOTIFICATIONS: 100,          // Recent notification history
  COLLABORATION_REQUESTS: 20,  // Low-frequency feature
  WATCHLIST_LIKES: 100,        // Popular watchlists display
  USER_ACTIVITY: 20,           // Activity feed
} as const;

export interface UserMovieAction {
  movieId: string;
  movieTitle: string;
  moviePoster?: string;
  movieYear?: string;
  actionType: 'watchlist' | 'watched' | 'rating' | 'review';
  rating?: number;
  review?: string;
  isPrivate?: boolean;
  timestamp: Timestamp;
}

// UserWatchlist interface imported from shared package

// UserProfile interface imported from shared package

export interface UserMovieData {
  uid: string;
  movieId: string;
  isInWatchlist: boolean;
  isWatched: boolean;
  rating?: number;
  review?: string;
  reviewVisibility?: 'anyone' | 'only-me'; // Review privacy setting
  watchedDate?: Timestamp;
  isFavorite: boolean;
  tags?: string[];
  notes?: string;
  watchCount: number;
  lastWatchedDate?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface DetailedMovieState extends MovieState {
  movieTitle: string;
  moviePoster?: string;
  movieYear?: string;
  movieGenre?: string;
  movieRuntime?: string;
  imdbRating?: string;
}

// UserPreferences interface imported from shared package


export interface UserActivity {
  id: string;
  uid: string;
  type: 'movie_rated' | 'movie_watched' | 'list_created' | 'review_posted' | 'movie_added_to_list' | 'watchlist_liked' | 'user_followed' | 'watchlist_shared' | 'collaborator_added' | 'watchlist_created';
  movieId?: string;
  listId?: string;
  reviewId?: string;
  watchlistId?: string;
  targetUserId?: string;
  collaboratorId?: string;
  rating?: number;
  isPrivate: boolean;
  metadata?: {
    movieTitle?: string;
    moviePoster?: string;
    listName?: string;
    watchlistName?: string;
    watchlistDescription?: string;
    watchlistIsPrivate?: boolean;
    watchlistMovieCount?: number;
    watchlistPosters?: string[];
    targetUserName?: string;
    targetUserAvatar?: string;
    targetUserStats?: {
      followers: number;
      watchlists: number;
    };
    collaboratorName?: string;
    collaboratorAvatar?: string;
    shareMethod?: 'link' | 'email' | 'social';
    userName?: string;
    [key: string]: any;
  };
  createdAt: Timestamp;
}

export interface UserFollowing {
  followerUid: string;
  followingUid: string;
  createdAt: Timestamp;
}

export interface RecentSearch {
  id: string;
  query: string;
  searchType: 'movie' | 'person' | 'user';
  resultCount: number;
  timestamp: Timestamp;
}

export interface UserSearchHistory {
  uid: string;
  recentSearches: RecentSearch[];
  popularSearches: string[]; // Most frequently searched terms
  searchStats: {
    totalSearches: number;
    movieSearches: number;
    personSearches: number;
    userSearches: number;
  };
  updatedAt: Timestamp;
}

export interface UserRecentMovies {
  uid: string;
  recentMovies: Array<{
    movieId: string;
    movieData: TMDbMovie;
    accessedAt: Timestamp;
  }>;
  maxMovies: number; // Maximum number of recent movies to keep (default: 10)
  updatedAt: Timestamp;
}

export interface UserRecentlyAddedMovies {
  uid: string;
  recentlyAddedMovies: Array<{
    movieId: string;
    movieData: TMDbMovie;
    addedAt: Timestamp;
  }>;
  lastRefresh: Timestamp; // When the list was last updated from TMDb
  updatedAt: Timestamp;
}

class FirebaseService {
  // Platform check - cached for performance
  private isNative: boolean = false;

  constructor() {
    this.isNative = isCapacitorNative();
    if (this.isNative) {
      console.log('FirebaseService: Running on native platform, using native Firebase plugins');
    }
  }

  /**
   * Extract first name from display name for username generation
   */
  private extractFirstName(displayName: string): string {
    if (!displayName) return 'user';

    // Split by space and take first word
    const firstName = displayName.trim().split(' ')[0];

    // Clean up the first name - remove special characters, keep only letters and numbers
    const cleanedName = firstName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 15); // Limit length

    return cleanedName || 'user';
  }

  // Initialize or get user profile
  async initializeUserProfile(user: User): Promise<UserProfile> {
    // Use native Firebase on native platforms for faster performance
    if (this.isNative) {
      const existingProfile = await nativeFirebase.getDocument<UserProfile>('userProfiles', user.uid);

      if (!existingProfile) {
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        const firstName = this.extractFirstName(displayName);
        const uniqueUsername = await authService.generateUniqueUsername(firstName);

        // Check if this is a test account for App Store/Play Store review
        const isTestAccount = user.email && TEST_ACCOUNT_EMAILS && Array.isArray(TEST_ACCOUNT_EMAILS) && TEST_ACCOUNT_EMAILS.includes(user.email);
        const initialRole: UserRole = (isTestAccount || !getWaitlistEnabled()) ? (USER_ROLES.REGULAR as UserRole) : (USER_ROLES.PENDING as UserRole);

        const newProfile: UserProfile = {
          uid: user.uid,
          email: user.email || '',
          displayName: displayName,
          username: uniqueUsername,
          ...(user.photoURL && { photoURL: user.photoURL }), // Only include if not null/undefined
          favoriteGenres: [],
          privacy: {
            profileVisibility: 'public',
            showWatchlist: true,
            showRatings: true,
            showActivity: true,
          },
          stats: {
            moviesWatched: 0,
            reviewsWritten: 0,
            listsCreated: 0,
            followersCount: 0,
            followingCount: 0,
            likesReceived: 0,
          },
          role: initialRole,
          createdAt: nativeFirebase.getServerTimestamp() as unknown as Timestamp,
          updatedAt: nativeFirebase.getServerTimestamp() as unknown as Timestamp,
        };

        console.log('🔐 [Native] Creating user profile:', {
          uid: user.uid,
          email: user.email,
          displayName: displayName,
          username: uniqueUsername,
          role: initialRole,
          isTestAccount: isTestAccount
        });
        await nativeFirebase.setDocument('userProfiles', user.uid, newProfile);

        if (initialRole === USER_ROLES.PENDING) {
          try {
            await accessControlService.createAccessRequest(
              user.uid,
              user.email || '',
              displayName,
              user.photoURL || undefined
            );
          } catch (e) {
            console.error('⚠️ Failed to auto-create access request (native):', e);
          }
        }

        console.log('✅ [Native] User profile created successfully:', {
          uid: user.uid,
          username: uniqueUsername,
          role: initialRole
        });

        return newProfile;
      }

      // If waitlist is disabled and user is stuck as pending, upgrade them to regular.
      if (!getWaitlistEnabled() && existingProfile.role === USER_ROLES.PENDING) {
        await nativeFirebase.updateDocument('userProfiles', user.uid, { role: USER_ROLES.REGULAR });
        try { await unifiedDeleteDoc('accessRequests', user.uid); } catch (_) {}
        existingProfile.role = USER_ROLES.REGULAR as UserRole;
      }

      return existingProfile;
    }

    // Web SDK path
    const userDoc = await unifiedGetDoc('userProfiles', user.uid);

    if (!userDoc.exists()) {
      // Use Google account display name as-is
      const displayName = user.displayName || user.email?.split('@')[0] || 'User';

      // Generate username from first name only
      const firstName = this.extractFirstName(displayName);
      const uniqueUsername = await authService.generateUniqueUsername(firstName);

      // Check if this is a test account for App Store/Play Store review
      const isTestAccount = user.email && TEST_ACCOUNT_EMAILS && Array.isArray(TEST_ACCOUNT_EMAILS) && TEST_ACCOUNT_EMAILS.includes(user.email);
      const initialRole: UserRole = (isTestAccount || !getWaitlistEnabled()) ? (USER_ROLES.REGULAR as UserRole) : (USER_ROLES.PENDING as UserRole);

      const newProfile: UserProfile = {
        uid: user.uid,
        email: user.email || '',
        displayName: displayName, // Keep full display name from Google
        username: uniqueUsername, // First name + numbers if needed
        ...(user.photoURL && { photoURL: user.photoURL }), // Only include if not null/undefined
        favoriteGenres: [],
        privacy: {
          profileVisibility: 'public',
          showWatchlist: true,
          showRatings: true,
          showActivity: true,
        },
        stats: {
          moviesWatched: 0,
          reviewsWritten: 0,
          listsCreated: 0,
          followersCount: 0,
          followingCount: 0,
          likesReceived: 0,
        },
        role: initialRole,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
      };

      console.log('🔐 Creating user profile with new naming logic:', {
        originalDisplayName: user.displayName,
        email: user.email,
        finalDisplayName: newProfile.displayName,
        extractedFirstName: firstName,
        finalUsername: uniqueUsername,
        role: initialRole,
        isTestAccount: isTestAccount
      });

      await unifiedSetDoc('userProfiles', user.uid, newProfile);

      if (initialRole === USER_ROLES.PENDING) {
        try {
          await accessControlService.createAccessRequest(
            user.uid,
            user.email || '',
            displayName,
            user.photoURL || undefined
          );
        } catch (e) {
          console.error('⚠️ Failed to auto-create access request:', e);
        }
      }

      console.log('✅ User profile created successfully:', {
        uid: user.uid,
        username: uniqueUsername,
        role: initialRole
      });

      return newProfile;
    }

    const existingProfile = userDoc.data() as UserProfile;

    // If waitlist is disabled and user is stuck as pending, upgrade them to regular.
    if (!getWaitlistEnabled() && existingProfile.role === USER_ROLES.PENDING) {
      await unifiedUpdateDoc('userProfiles', user.uid, { role: USER_ROLES.REGULAR });
      try { await unifiedDeleteDoc('accessRequests', user.uid); } catch (_) {}
      existingProfile.role = USER_ROLES.REGULAR as UserRole;
    }

    return existingProfile;
  }


  // --- Default Watchlist Settings ---
  async getDefaultWatchlistSettings(userId: string): Promise<Record<string, any>> {
    const userDoc = await unifiedGetDoc('users', userId);
    if (!userDoc.exists()) return {};
    const data = userDoc.data();
    return data?.defaultWatchlistSettings || {};
  }

  async updateDefaultWatchlistSettings(userId: string, watchlistType: string, updates: Record<string, any>): Promise<void> {
    const userDoc = await unifiedGetDoc('users', userId);
    let prevSettings: Record<string, any> = {};
    if (userDoc.exists()) {
      prevSettings = userDoc.data()?.defaultWatchlistSettings || {};
    }
    const newSettings = {
      ...prevSettings,
      [watchlistType]: {
        ...(prevSettings[watchlistType] || {}),
        ...updates
      }
    };
    await unifiedUpdateDoc('userProfiles', userId, { defaultWatchlistSettings: newSettings });
  }

  // Add movie to user's watchlist
  async addToWatchlist(user: User, movieId: string, movieTitle: string, moviePoster?: string, movieYear?: string): Promise<void> {
    try {
      // Get existing data to preserve watched status
      const existingData = await this.getUserMovieData(user, movieId);

      // Use the newer UserMovieData collection approach
      await this.saveUserMovieData(user, movieId, {
        isInWatchlist: true,
        // Preserve existing watched status (don't default to false)
        isWatched: existingData?.isWatched,
        rating: existingData?.rating,
        review: existingData?.review,
        watchedDate: existingData?.watchedDate,
      });

      // Create activity entry
      await this.addWatchlistActivity(user, movieId, movieTitle, moviePoster);

      // Also save to localStorage for offline access
      const localWatchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
      if (!localWatchlist.includes(movieId)) {
        localWatchlist.push(movieId);
        localStorage.setItem('watchlist', JSON.stringify(localWatchlist));
      }

      console.log('Firebase addToWatchlist completed successfully');
    } catch (error) {
      console.error('Firebase addToWatchlist error:', error);
      throw error;
    }
  }

  // Remove movie from user's watchlist
  async removeFromWatchlist(user: User, movieId: string): Promise<void> {
    try {
      // Use the newer UserMovieData collection approach
      const existingData = await this.getUserMovieData(user, movieId);
      if (existingData) {
        if (existingData.isWatched) {
          // Keep the data but mark as not in watchlist
          await this.saveUserMovieData(user, movieId, {
            isInWatchlist: false,
          });
        } else {
          // Remove completely if not watched
          const docId = `${user.uid}_${movieId}`;

          // Use native Firebase on native platforms
          if (this.isNative) {
            await nativeFirebase.deleteDocument('userMovies', docId);
          } else {
            const userMovieRef = doc(getDb(), 'userMovies', docId);
            await unifiedDeleteDoc('userMovies', docId);
          }
        }
      }

      // Also remove from localStorage
      const localWatchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
      const updatedWatchlist = localWatchlist.filter((id: string) => id !== movieId);
      localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));

      console.log('Firebase removeFromWatchlist completed successfully');
    } catch (error) {
      console.error('Firebase removeFromWatchlist error:', error);
      throw error;
    }
  }

  // markAsWatched - REMOVED: Now available in shared/movieService.ts (~58 lines removed)

  // unmarkAsWatched - REMOVED: Now available in shared/movieService.ts (~34 lines removed)

  // Rate a movie
  async rateMovie(user: User, movieId: string, movieTitle: string, rating: number, moviePoster?: string, movieYear?: string): Promise<void> {
    const userRef = doc(getDb(), 'users', user.uid);

    // Create action object without serverTimestamp for arrayUnion
    const action: Omit<UserMovieAction, 'timestamp'> & { timestamp: Date } = {
      movieId,
      movieTitle,
      ...(moviePoster && { moviePoster }), // Only include if not undefined
      ...(movieYear && { movieYear }), // Only include if not undefined
      actionType: 'rating',
      rating,
      timestamp: new Date() // Use regular Date instead of serverTimestamp
    };

    await unifiedUpdateDoc('userProfiles', userId, {
      [`ratings.${movieId}`]: rating,
      movieActions: arrayUnion(action)
    });

    // Also save to localStorage
    const localRatings = JSON.parse(localStorage.getItem('ratings') || '{}');
    localRatings[movieId] = rating;
    localStorage.setItem('ratings', JSON.stringify(localRatings));
  }

  // Add or update a review
  async reviewMovie(user: User, movieId: string, movieTitle: string, review: string, rating?: number, isPrivate?: boolean, moviePoster?: string, movieYear?: string): Promise<void> {
    const userRef = doc(getDb(), 'users', user.uid);

    // Create action object without serverTimestamp for arrayUnion
    const action: Omit<UserMovieAction, 'timestamp'> & { timestamp: Date } = {
      movieId,
      movieTitle,
      ...(moviePoster && { moviePoster }), // Only include if not undefined
      ...(movieYear && { movieYear }), // Only include if not undefined
      actionType: 'review',
      ...(rating && { rating }), // Only include if not undefined
      review: review.trim(),
      ...(isPrivate !== undefined && { isPrivate }), // Only include if not undefined
      timestamp: new Date() // Use regular Date instead of serverTimestamp
    };

    const updateData: any = {
      [`reviews.${movieId}`]: {
        rating: rating || null,
        review: review.trim(),
        isPrivate: isPrivate || false,
        date: new Date() // Use regular Date instead of serverTimestamp
      },
      movieActions: arrayUnion(action)
    };

    if (rating) {
      updateData[`ratings.${movieId}`] = rating;
    }

    await unifiedUpdateDoc('users', user.uid, updateData);

    // Also save to localStorage
    if (rating) {
      const localRatings = JSON.parse(localStorage.getItem('ratings') || '{}');
      localRatings[movieId] = rating;
      localStorage.setItem('ratings', JSON.stringify(localRatings));
    }

    const reviewObj = {
      id: 'user-review',
      userId: user.uid,
      userName: user.displayName || 'You',
      userAvatar: user.photoURL,
      rating,
      review: review.trim(),
      date: formatFirebaseDate(new Date(), undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      isPrivate: isPrivate || false
    };
    localStorage.setItem(`review_${movieId}`, JSON.stringify(reviewObj));
  }

  // REMOVED: getUserProfile - now available in shared/authService.ts

  // REMOVED: getUserProfileLite - now available in shared/authService.ts

  // Sync localStorage data with Firebase (for offline-first approach)
  async syncLocalData(user: User): Promise<void> {
    try {
      const watchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
      const watched = JSON.parse(localStorage.getItem('watched') || '[]');
      const ratings = JSON.parse(localStorage.getItem('ratings') || '{}');

      const userRef = doc(getDb(), 'userProfiles', user.uid);
      await unifiedUpdateDoc('userProfiles', userId, {
        watchlist,
        watched,
        ratings
      });
    } catch (error) {
      console.error('Error syncing local data:', error);
    }
  }

  // Load user data from Firebase to localStorage (when user logs in)
  async loadUserDataToLocal(user: User): Promise<void> {
    try {
      // Load watchlist movies
      const watchlistMovies = await this.getUserWatchlistMovies(user);
      localStorage.setItem('watchlist', JSON.stringify(watchlistMovies));

      // Load watched movies
      const watchedMovies = await this.getUserWatchedMovies(user);
      const watchedIds = watchedMovies.map(movie => movie.movieId);
      localStorage.setItem('watched', JSON.stringify(watchedIds));

      // Load ratings
      const ratings: { [movieId: string]: number } = {};
      watchedMovies.forEach(movie => {
        if (movie.rating) {
          ratings[movie.movieId] = movie.rating;
        }
      });
      localStorage.setItem('ratings', JSON.stringify(ratings));

      // Load reviews
      watchedMovies.forEach(movie => {
        if (movie.review) {
          const reviewObj = {
            id: 'user-review',
            userId: user.uid,
            userName: user.displayName || 'You',
            userAvatar: user.photoURL,
            rating: movie.rating,
            review: movie.review,
            date: formatFirebaseDate(movie.watchedDate, 'Recently', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            isPrivate: false
          };
          localStorage.setItem(`review_${movie.movieId}`, JSON.stringify(reviewObj));
        }
      });
    } catch (error) {
      console.error('Error loading user data from Firebase:', error);
    }
  }

  // Get user's recent movie actions
  async getUserActions(userId: string, limit: number = 10): Promise<UserMovieAction[]> {
    try {
      // Use the UserActivity collection instead of UserProfile.movieActions
      const activities = await this.getUserActivity(userId, limit);

      // Convert UserActivity to UserMovieAction format
      return activities
        .filter(activity => activity.movieId) // Only movie-related activities
        .map(activity => ({
          movieId: activity.movieId!,
          movieTitle: activity.metadata?.movieTitle || 'Unknown Movie',
          moviePoster: activity.metadata?.moviePoster,
          movieYear: activity.metadata?.movieYear,
          actionType: activity.type === 'movie_watched' ? 'watched' :
            activity.type === 'movie_rated' ? 'rating' :
              activity.type === 'review_posted' ? 'review' : 'watchlist',
          rating: activity.rating,
          review: activity.metadata?.review,
          isPrivate: activity.isPrivate,
          timestamp: activity.createdAt,
        }));
    } catch (error) {
      console.error('Error getting user actions:', error);
      return [];
    }
  }

  // createWatchlist - REMOVED: Now available in shared/watchlistService.ts

  // getUserWatchlists - REMOVED: Now available in shared/watchlistService.ts

  // addMovieToWatchlist - REMOVED: Now available in shared/watchlistService.ts

  // 🚀 NEW: Comprehensive duplicate cleanup function for immediate use
  async cleanupWatchlistDuplicates(watchlistId: string): Promise<{
    originalCount: number;
    cleanedCount: number;
    duplicatesRemoved: number;
    duplicateInfo: Array<{ originalId: string; standardizedId: string; action: string }>;
  }> {
    console.log('🧹 FirebaseService: Starting comprehensive duplicate cleanup for watchlist:', watchlistId);

    const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

    if (!watchlistDoc.exists()) {
      throw new Error('Watchlist not found');
    }

    const watchlistData = watchlistDoc.data();
    const movieDetails = watchlistData.movieDetails || [];
    const movies = watchlistData.movies || [];

    console.log('🧹 FirebaseService: Original data:', {
      movieDetailsCount: movieDetails.length,
      moviesArrayCount: movies.length
    });

    // Use the same duplicate detection logic as the frontend
    const { cleanedMovies, duplicatesRemoved, duplicateInfo } = this.deduplicateMovieArray(movieDetails);

    // Also clean up the movies array
    const uniqueMovieIds = Array.from(new Set(movies));
    const movieArrayDuplicates = movies.length - uniqueMovieIds.length;

    const totalDuplicatesRemoved = duplicatesRemoved + movieArrayDuplicates;

    if (totalDuplicatesRemoved > 0) {
      console.log('🧹 FirebaseService: Removing', totalDuplicatesRemoved, 'duplicates from watchlist');

      // Update the watchlist with cleaned data
      await unifiedUpdateDoc('watchlists', watchlistId, {
        movieDetails: cleanedMovies,
        movies: uniqueMovieIds,
        updatedAt: serverTimestamp()
      });

      console.log('✅ FirebaseService: Duplicate cleanup completed successfully');
    } else {
      console.log('✅ FirebaseService: No duplicates found, watchlist is already clean');
    }

    return {
      originalCount: movieDetails.length,
      cleanedCount: cleanedMovies.length,
      duplicatesRemoved: totalDuplicatesRemoved,
      duplicateInfo
    };
  }

  // Helper function to deduplicate movie array (consistent with frontend logic)
  private deduplicateMovieArray(movieDetails: any[]): {
    cleanedMovies: any[];
    duplicatesRemoved: number;
    duplicateInfo: Array<{ originalId: string; standardizedId: string; action: string }>;
  } {
    const processedIds = new Set<string>();
    const cleanedMovies: any[] = [];
    const duplicateInfo: Array<{ originalId: string; standardizedId: string; action: string }> = [];

    movieDetails.forEach((movie) => {
      // Use the same standardization logic as frontend
      const standardizedId = getStandardizedMovieId(movie);

      if (!standardizedId || standardizedId === 'unknown') {
        duplicateInfo.push({
          originalId: movie.id || movie.imdbID || 'unknown',
          standardizedId: 'unknown',
          action: 'REMOVED_INVALID'
        });
        return; // Skip invalid movies
      }

      if (processedIds.has(standardizedId)) {
        duplicateInfo.push({
          originalId: movie.id || movie.imdbID || 'unknown',
          standardizedId,
          action: 'REMOVED_DUPLICATE'
        });
        return; // Skip duplicates
      }

      processedIds.add(standardizedId);
      cleanedMovies.push(movie);

      duplicateInfo.push({
        originalId: movie.id || movie.imdbID || 'unknown',
        standardizedId,
        action: 'KEPT'
      });
    });

    return {
      cleanedMovies,
      duplicatesRemoved: movieDetails.length - cleanedMovies.length,
      duplicateInfo
    };
  }

  // removeMovieFromWatchlist - REMOVED: Now available in shared/watchlistService.ts

  // Update movie note in a specific watchlist
  async updateMovieNote(watchlistId: string, movieId: string, note: string, userUid?: string): Promise<void> {
    // Get current watchlist data to find and update the movie
    const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);
    if (!watchlistDoc.exists()) {
      throw new Error('Watchlist not found');
    }

    const watchlistData = watchlistDoc.data();
    const movieDetails = watchlistData.movieDetails || [];

    // Find and update the movie with the note
    const updatedMovieDetails = movieDetails.map((movie: any) => {
      if (movie.id === movieId || movie.imdbID === movieId) {
        return {
          ...movie,
          note: note.trim(),
          lastModifiedBy: userUid // Track who modified this movie's note
        };
      }
      return movie;
    });

    // Update the watchlist with the modified movie details
    const updateData: any = {
      movieDetails: updatedMovieDetails,
      updatedAt: serverTimestamp()
    };

    // Set lastModifiedBy on the watchlist level if userUid is provided
    if (userUid) {
      updateData.lastModifiedBy = userUid;
    }

    await unifiedUpdateDoc('watchlists', watchlistId, updateData);
  }

  // Update default watchlists (want-to-watch, watched, etc.)
  async updateDefaultWatchlist(user: User, listType: string, movieIds: string[]): Promise<void> {
    const userRef = doc(getDb(), 'userProfiles', user.uid);
    await unifiedUpdateDoc('userProfiles', userId, {
      [`defaultWatchlists.${listType}`]: movieIds
    });
  }

  // Get comprehensive user state for app initialization
  async getUserAppState(user: User): Promise<{
    profile: UserProfile;
    watchlists: UserWatchlist[];
    defaultMovies: {
      watchlist: string[];
      watched: string[];
      ratings: { [movieId: string]: number };
    };
  }> {
    const profile = await getCachedUserProfile(user.uid);
    if (!profile) {
      throw new Error('User profile not found');
    }

    const watchlists = await watchlistService.getUserWatchlists(user.uid);

    // Get watchlist and watched data from the new methods
    const watchlistMovies = await this.getUserWatchlistMovies(user);
    const watchedMovies = await this.getUserWatchedMovies(user);
    const watchedIds = watchedMovies.map(movie => movie.movieId);

    // Extract ratings from watched movies
    const ratings: { [movieId: string]: number } = {};
    watchedMovies.forEach(movie => {
      if (movie.rating) {
        ratings[movie.movieId] = movie.rating;
      }
    });

    return {
      profile,
      watchlists,
      defaultMovies: {
        watchlist: watchlistMovies,
        watched: watchedIds,
        ratings
      }
    };
  }

  // Save complete app state
  async saveUserAppState(user: User, state: {
    watchlist: string[];
    watched: string[];
    ratings: { [movieId: string]: number };
    defaultWatchlists?: { [listType: string]: string[] };
  }): Promise<void> {
    // Since we now use UserMovieData collection, this method should update individual movie records
    // For now, we'll implement a basic approach that updates existing records
    try {
      const batch = writeBatch(getDb());

      // Update watchlist movies
      for (const movieId of state.watchlist) {
        const userMovieRef = doc(getDb(), 'userMovies', `${user.uid}_${movieId}`);
        const movieData: any = {
          uid: user.uid,
          movieId,
          isInWatchlist: true,
          isWatched: state.watched.includes(movieId),
          isFavorite: false,
          tags: [],
          watchCount: state.watched.includes(movieId) ? 1 : 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        // Only add rating if it exists and is not undefined
        if (state.ratings[movieId] !== undefined) {
          movieData.rating = state.ratings[movieId];
        }

        batch.set(userMovieRef, movieData, { merge: true });
      }

      // Update watched movies
      for (const movieId of state.watched) {
        const userMovieRef = doc(getDb(), 'userMovies', `${user.uid}_${movieId}`);
        const movieData: any = {
          uid: user.uid,
          movieId,
          isInWatchlist: true, // Watched movies are automatically in watchlist
          isWatched: true,
          isFavorite: false,
          tags: [],
          watchCount: 1,
          watchedDate: serverTimestamp(),
          lastWatchedDate: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        // Only add rating if it exists and is not undefined
        if (state.ratings[movieId] !== undefined) {
          movieData.rating = state.ratings[movieId];
        }

        batch.set(userMovieRef, movieData, { merge: true });
      }

      await batch.commit();
    } catch (error) {
      console.error('Error saving app state:', error);
      throw error;
    }
  }

  // Delete user movie data for a specific movie
  async deleteUserMovieData(user: User, movieId: string): Promise<void> {
    try {
      const docId = `${user.uid}_${movieId}`;

      // Use native Firebase on native platforms
      if (this.isNative) {
        await nativeFirebase.deleteDocument('userMovies', docId);
        console.log(`[Native] Successfully deleted movie data for ${movieId}`);
        return;
      }

      // Web SDK path
      const userMovieRef = doc(getDb(), 'userMovies', docId);
      await unifiedDeleteDoc('userMovies', docId);
      console.log(`Successfully deleted movie data for ${movieId}`);
    } catch (error) {
      console.error(`Error deleting movie data for ${movieId}:`, error);
      throw error;
    }
  }

  // Save or update movie data for a user
  async saveUserMovieData(
    user: User,
    movieId: string,
    data: Partial<UserMovieData>
  ): Promise<void> {
    try {
      // Normalize movieId to prevent double-prefixed IDs like tmdb_tmdb_XXX
      const normalizedMovieId = migrateMovieId(movieId);
      if (normalizedMovieId !== movieId) {
        console.log(`firebaseService.saveUserMovieData: Normalized movieId from "${movieId}" to "${normalizedMovieId}"`);
      }

      const docId = `${user.uid}_${normalizedMovieId}`;
      const existingData = await this.getUserMovieData(user, normalizedMovieId);

      // Use appropriate timestamp based on platform
      const getTimestamp = () => this.isNative
        ? nativeFirebase.getServerTimestamp() as unknown as Timestamp
        : serverTimestamp() as Timestamp;

      const updatedData: UserMovieData = {
        uid: user.uid,
        movieId: normalizedMovieId,
        isInWatchlist: data.isInWatchlist ?? existingData?.isInWatchlist ?? false,
        isWatched: data.isWatched ?? existingData?.isWatched ?? false,
        rating: data.rating ?? existingData?.rating,
        review: data.review ?? existingData?.review,
        reviewVisibility: data.reviewVisibility ?? existingData?.reviewVisibility,
        watchedDate: data.watchedDate ?? existingData?.watchedDate,
        isFavorite: data.isFavorite ?? existingData?.isFavorite ?? false,
        tags: data.tags ?? existingData?.tags ?? [],
        notes: data.notes ?? existingData?.notes,
        watchCount: data.isWatched ? (existingData?.watchCount ?? 0) + 1 : existingData?.watchCount ?? 0,
        lastWatchedDate: data.isWatched ? (data.watchedDate ?? getTimestamp()) : existingData?.lastWatchedDate,
        createdAt: existingData?.createdAt ?? getTimestamp(),
        updatedAt: getTimestamp(),
      };

      // If adding to watchlist for first time, set the date
      if (data.isInWatchlist && !existingData?.isInWatchlist && !updatedData.watchedDate) {
        updatedData.watchedDate = Timestamp.fromDate(new Date());
      }

      // If marking as watched for first time, set the date but DON'T automatically add to watchlist
      if (data.isWatched && !existingData?.isWatched) {
        if (!updatedData.watchedDate) {
          updatedData.watchedDate = Timestamp.fromDate(new Date());
        }
      }

      // Filter out undefined values before sending to Firebase
      const cleanedData: any = {};
      Object.entries(updatedData).forEach(([key, value]) => {
        if (value !== undefined) {
          cleanedData[key] = value;
        }
      });

      // Use native Firebase on native platforms
      if (this.isNative) {
        await nativeFirebase.setDocument('userMovies', docId, cleanedData);
        console.log('[Native] Movie data saved successfully');
      } else {
        const userMovieRef = doc(getDb(), 'userMovies', docId);
        await unifiedSetDoc('userMovies', docId, cleanedData);
        console.log('Movie data saved successfully');
      }
    } catch (error) {
      console.error('Error saving movie data:', error);
      throw error;
    }
  }

  // Get movie data for a specific user and movie
  async getUserMovieData(
    user: User,
    movieId: string
  ): Promise<UserMovieData | null> {
    try {
      // Normalize movieId to handle double-prefixed IDs like tmdb_tmdb_XXX
      const normalizedMovieId = migrateMovieId(movieId);
      const docId = `${user.uid}_${normalizedMovieId}`;

      // Use native Firebase on native platforms
      if (this.isNative) {
        return await nativeFirebase.getDocument<UserMovieData>('userMovies', docId);
      }

      // Web SDK path
      const docSnap = await unifiedGetDoc('userMovies', docId);

      if (docSnap.exists()) {
        return docSnap.data() as UserMovieData;
      }
      return null;
    } catch (error) {
      console.error('Error getting movie data:', error);
      return null;
    }
  }

  // Get all watchlist movies for a user
  async getUserWatchlistMovies(user: User): Promise<string[]> {
    try {
      // Use native Firebase on native platforms
      if (this.isNative) {
        const results = await nativeFirebase.queryDocuments<UserMovieData>(
          'userMovies',
          [
            { field: 'uid', operator: '==', value: user.uid },
            { field: 'isInWatchlist', operator: '==', value: true }
          ],
          { limitCount: QUERY_LIMITS.USER_MOVIES }
        );
        return results.map(doc => doc.movieId);
      }

      // Web SDK path
      const q = query(
        collection(getDb(), 'userMovies'),
        where('uid', '==', user.uid),
        where('isInWatchlist', '==', true),
        limit(QUERY_LIMITS.USER_MOVIES)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data().movieId);
    } catch (error) {
      console.error('Error getting watchlist movies:', error);
      return [];
    }
  }

  // Get all watched movies for a user
  async getUserWatchedMovies(user: User): Promise<UserMovieData[]> {
    try {
      // Use native Firebase on native platforms
      if (this.isNative) {
        const results = await nativeFirebase.queryDocuments<UserMovieData>(
          'userMovies',
          [
            { field: 'uid', operator: '==', value: user.uid },
            { field: 'isWatched', operator: '==', value: true }
          ],
          {
            orderByField: 'watchedDate',
            orderDirection: 'desc',
            limitCount: QUERY_LIMITS.USER_MOVIES
          }
        );
        return results;
      }

      // Web SDK path
      const q = query(
        collection(getDb(), 'userMovies'),
        where('uid', '==', user.uid),
        where('isWatched', '==', true),
        orderBy('watchedDate', 'desc'),
        limit(QUERY_LIMITS.USER_MOVIES)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data() as UserMovieData);
    } catch (error) {
      console.error('Error getting watched movies:', error);
      return [];
    }
  }

  // Get ALL user movie data (comprehensive method)
  async getAllUserMovieData(user: User): Promise<UserMovieData[]> {
    try {
      console.log('FirebaseService: Fetching ALL user movie data for user:', user.uid);

      // Use native Firebase on native platforms for faster queries
      if (this.isNative) {
        const allMovieData = await nativeFirebase.queryDocuments<UserMovieData>(
          'userMovies',
          [{ field: 'uid', operator: '==', value: user.uid }]
        );

        console.log(`FirebaseService: [Native] Retrieved ${allMovieData.length} total movie records`);
        return allMovieData;
      }

      // Web SDK path
      const q = query(
        collection(getDb(), 'userMovies'),
        where('uid', '==', user.uid)
      );

      const querySnapshot = await getDocs(q);
      const allMovieData = querySnapshot.docs.map(doc => {
        const data = doc.data() as UserMovieData;
        console.log('FirebaseService: Found movie data:', data.movieId, {
          isInWatchlist: data.isInWatchlist,
          isWatched: data.isWatched,
          rating: data.rating,
          hasReview: !!data.review
        });
        return data;
      });

      console.log('FirebaseService: Retrieved', allMovieData.length, 'total movie records for user');
      console.log('FirebaseService: Breakdown:', {
        watchlistMovies: allMovieData.filter(m => m.isInWatchlist).length,
        watchedMovies: allMovieData.filter(m => m.isWatched).length,
        ratedMovies: allMovieData.filter(m => m.rating).length,
        reviewedMovies: allMovieData.filter(m => m.review).length,
      });

      return allMovieData;
    } catch (error) {
      console.error('Error getting all user movie data:', error);
      return [];
    }
  }

  // Get movies watched this year for a user
  async getUserWatchedThisYear(user: User): Promise<UserMovieData[]> {
    try {
      const currentYear = new Date().getFullYear();
      const yearStart = new Date(currentYear, 0, 1); // January 1st of current year
      const yearEnd = new Date(currentYear + 1, 0, 1); // January 1st of next year

      let allWatched: UserMovieData[];

      // Use native Firebase on native platforms
      if (this.isNative) {
        allWatched = await nativeFirebase.queryDocuments<UserMovieData>(
          'userMovies',
          [
            { field: 'uid', operator: '==', value: user.uid },
            { field: 'isWatched', operator: '==', value: true }
          ]
        );
      } else {
        // Web SDK path
        const q = query(
          collection(getDb(), 'userMovies'),
          where('uid', '==', user.uid),
          where('isWatched', '==', true)
        );

        const querySnapshot = await getDocs(q);
        allWatched = querySnapshot.docs.map(doc => doc.data() as UserMovieData);
      }

      // Filter by current year (client-side filtering works on both platforms)
      return allWatched.filter(movie => {
        if (!movie.watchedDate) return false;

        // Convert Timestamp to Date for comparison
        const watchedDate = movie.watchedDate instanceof Timestamp
          ? movie.watchedDate.toDate()
          : new Date(movie.watchedDate as any);

        return watchedDate >= yearStart && watchedDate < yearEnd;
      });
    } catch (error) {
      console.error('Error getting movies watched this year:', error);
      return [];
    }
  }

  // saveUserWatchlist - REMOVED: Now available in shared/watchlistService.ts (~57 lines removed)
  // Temporary delegation method for backward compatibility
  async saveUserWatchlist(user: User, watchlistData: any): Promise<string> {
    return await watchlistService.saveUserWatchlist(user, watchlistData);
  }

  // updateUserWatchlist - REMOVED: Now available in shared/watchlistService.ts (~28 lines removed)
  // Temporary delegation method for backward compatibility
  async updateUserWatchlist(watchlistId: string, updates: any): Promise<void> {
    return await watchlistService.updateUserWatchlist(watchlistId, updates);
  }

  // deleteUserWatchlist - REMOVED: Now available in shared/watchlistService.ts (~9 lines removed)
  // Temporary delegation method for backward compatibility
  async deleteUserWatchlist(watchlistId: string): Promise<void> {
    return await watchlistService.deleteUserWatchlist(watchlistId);
  }

  // getMovieReviews - REMOVED: Now available in shared/movieService.ts (~73 lines removed)
  // Temporary delegation method for backward compatibility
  async getMovieReviews(movieId: string, viewerUserId?: string) {
    return await movieService.getMovieReviews(movieId, viewerUserId);
  }

  // getMovieRatings - REMOVED: Now available in shared/movieService.ts (~33 lines removed)

  // Update user's review for a movie
  async updateMovieReview(
    user: User,
    movieId: string,
    movieTitle: string,
    review: string,
    rating?: number,
    isPrivate: boolean = false,
    moviePoster?: string,
    movieYear?: string
  ): Promise<string> {
    try {
      // Update the review in UserMovieData
      await this.saveUserMovieData(user, movieId, {
        review: review.trim(),
        rating: rating,
        isWatched: true, // Reviewing implies watching
        isInWatchlist: true, // Watched movies are automatically in watchlist
      });

      // Create a user activity entry
      await this.createUserActivity(user.uid, {
        type: 'review_posted',
        movieId,
        rating,
        isPrivate,
        metadata: {
          movieTitle,
          moviePoster,
          movieYear,
          review: review.trim()
        }
      });

      // Update localStorage as well
      const localReviews = JSON.parse(localStorage.getItem('reviews') || '{}');
      localReviews[movieId] = {
        id: 'user-review',
        userId: user.uid,
        userName: user.displayName || 'You',
        userAvatar: user.photoURL,
        rating,
        review: review.trim(),
        date: formatFirebaseDate(new Date()),
        isPrivate
      };
      localStorage.setItem('reviews', JSON.stringify(localReviews));

      return `${user.uid}-${movieId}`;
    } catch (error) {
      console.error('Error updating movie review:', error);
      throw error;
    }
  }

  // Delete rating and review completely and unwatch the movie
  async deleteMovieRatingAndReview(user: User, movieId: string): Promise<void> {
    console.log('Firebase: Starting deleteMovieRatingAndReview for movieId:', movieId);

    try {
      // Get existing data to check current state
      const existingData = await this.getUserMovieData(user, movieId);
      console.log('Firebase: Existing data before deletion:', existingData);

      if (existingData) {
        // Check if movie is in any watchlist
        const isInWatchlist = existingData.isInWatchlist;

        if (isInWatchlist) {
          // Keep the record but remove watched status, rating, and review completely
          const cleanedData = {
            uid: user.uid,
            movieId,
            isInWatchlist: true, // Keep in watchlist
            isWatched: false, // Unwatch the movie
            isFavorite: existingData.isFavorite || false,
            tags: existingData.tags || [],
            notes: existingData.notes,
            watchCount: 0, // Reset watch count
            createdAt: existingData.createdAt,
            updatedAt: serverTimestamp() as Timestamp,
            // rating, review, reviewVisibility, watchedDate, lastWatchedDate are completely omitted
          };

          const userMovieRef = doc(getDb(), 'userMovies', `${user.uid}_${movieId}`);
          await unifiedSetDoc('userMovies', `${user.uid}_${movieId}`, cleanedData);
          console.log('Firebase: Cleaned movie data - kept in watchlist but removed all watch/review data');

        } else {
          // Movie is not in watchlist, delete the entire record
          const userMovieRef = doc(getDb(), 'userMovies', `${user.uid}_${movieId}`);
          await unifiedDeleteDoc('userMovies', `${user.uid}_${movieId}`);
          console.log('Firebase: Completely deleted movie record - no longer in watchlist or watched');
        }
      } else {
        console.log('Firebase: No existing data found for movieId:', movieId);
      }

      // Remove from ALL localStorage locations
      const localRatings = JSON.parse(localStorage.getItem('ratings') || '{}');
      delete localRatings[movieId];
      localStorage.setItem('ratings', JSON.stringify(localRatings));

      // Remove from general reviews storage
      const localReviews = JSON.parse(localStorage.getItem('reviews') || '{}');
      delete localReviews[movieId];
      localStorage.setItem('reviews', JSON.stringify(localReviews));

      // Remove individual review storage
      localStorage.removeItem(`review_${movieId}`);

      // Remove from watched list in localStorage
      const localWatched = JSON.parse(localStorage.getItem('watched') || '[]');
      const updatedWatched = localWatched.filter((id: string) => id !== movieId);
      localStorage.setItem('watched', JSON.stringify(updatedWatched));

      console.log('Firebase deleteMovieRatingAndReview completed successfully - completely removed review data and unwatched movie');
    } catch (error) {
      console.error('Firebase deleteMovieRatingAndReview error:', error);
      throw error;
    }
  }

  // Utility function to get consistent movie state for any movie
  getMovieStateForDisplay(movieData: UserMovieData | null): MovieState | null {
    if (!movieData) return null;

    return {
      movieId: movieData.movieId,
      isInWatchlist: movieData.isInWatchlist,
      isWatched: movieData.isWatched,
      rating: movieData.rating,
      review: movieData.review,
      watchedDate: movieData.watchedDate instanceof Timestamp
        ? movieData.watchedDate.toDate().toISOString()
        : movieData.watchedDate ? String(movieData.watchedDate) : undefined,
      addedToWatchlistDate: movieData.watchedDate instanceof Timestamp
        ? movieData.watchedDate.toDate().toISOString()
        : movieData.watchedDate ? String(movieData.watchedDate) : undefined,
      updatedAt: movieData.updatedAt instanceof Timestamp
        ? movieData.updatedAt.toDate().toISOString()
        : String(movieData.updatedAt),
    };
  }

  // Utility function to format dates consistently
  formatMovieDate(dateString?: string): string {
    if (!dateString) return '';

    return formatFirebaseDate(dateString, dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Utility function to get movie status text
  getMovieStatusText(movieState: MovieState): string {
    if (movieState.isWatched) {
      if (movieState.rating) {
        return `Watched • Rated ${movieState.rating}/10`;
      }
      return 'Watched';
    } else if (movieState.isInWatchlist) {
      return 'In Watchlist';
    }
    return '';
  }

  // Save user preferences (for onboarding and settings)
  async saveUserPreferences(
    user: User,
    preferences: {
      preferredLanguages?: string[];
      onboardingCompleted?: boolean;
      onboardingCompletedAt?: string;
      [key: string]: any;
    }
  ): Promise<void> {
    try {
      const existingPrefs = await unifiedGetDoc('userPreferences', user.uid);

      const updateData = {
        ...preferences,
        updatedAt: new Date().toISOString(),
        userId: user.uid,
      };

      if (existingPrefs.exists()) {
        await unifiedUpdateDoc('userProfiles', userId, updateData);
      } else {
        await unifiedSetDoc('userProfiles', userId, {
          ...updateData,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error saving user preferences:', error);
      throw error;
    }
  }

  // getUserPreferences - REMOVED: Now available in shared/authService.ts (~18 lines removed)

  // Username Management
  async checkUsernameAvailability(username: string, excludeUid?: string): Promise<boolean> {
    if (!username || username.length < 3 || username.length > 30) {
      return false;
    }

    // Normalize username (lowercase, no @ symbol)
    const normalizedUsername = username.toLowerCase().replace(/^@/, '');

    // Check for invalid characters (only alphanumeric, underscore, hyphen allowed)
    const validUsernameRegex = /^[a-z0-9_-]+$/;
    if (!validUsernameRegex.test(normalizedUsername)) {
      return false;
    }

    try {
      const docs = await unifiedCollection('userProfiles').where('username', '==', normalizedUsername).getDocs();

      // If we need to exclude a specific UID, filter manually
      if (excludeUid) {
        const filtered = docs.filter(d => d.data().uid !== excludeUid);
        console.log(`🔍 Username "${normalizedUsername}" check: found ${docs.length} total, ${filtered.length} after excluding ${excludeUid}`);
        return filtered.length === 0;
      }

      console.log(`🔍 Username "${normalizedUsername}" check: found ${docs.length} users`);
      return docs.length === 0;
    } catch (error: any) {
      console.error('Error checking username availability:', error);
      throw error;
    }
  }



  async validateUsername(username: string): Promise<{ valid: boolean; message?: string; suggestions?: string[] }> {
    if (!username) {
      return { valid: false, message: 'Username is required' };
    }

    const normalizedUsername = username.toLowerCase().replace(/^@/, '');

    // Import content moderation utilities
    const { validateUsernameInput, getCleanUsernameSuggestions } = await import('../utils/contentModeration');

    // Use lightweight validation without inappropriate language filtering
    const inputValidation = validateUsernameInput(normalizedUsername);
    if (!inputValidation.isValid) {
      // Generate clean suggestions if validation fails
      const suggestions = getCleanUsernameSuggestions(normalizedUsername);
      return {
        valid: false,
        message: inputValidation.message,
        suggestions: suggestions.length > 0 ? suggestions : undefined
      };
    }

    return { valid: true };
  }

  // User Profile Management
  // createUserProfile - REMOVED: Now available in shared/authService.ts

  // updateUserProfile - REMOVED: Now available in shared/authService.ts

  /**
   * Clean up old username references when a user changes their username
   */
  private async cleanupOldUsernameReferences(userId: string, oldUsername: string, newUsername: string): Promise<void> {
    try {
      console.log('🧹 Cleaning up old username references:', { userId, oldUsername, newUsername });

      // Note: In our current implementation, usernames are primarily stored in the userProfiles collection
      // and generated dynamically in the UI. However, if we stored usernames in other places
      // (like activity metadata, notifications, etc.), we would clean them up here.

      // For now, we'll just log that the cleanup is complete since usernames are mainly
      // generated from the profile data and don't need extensive cleanup
      console.log('✅ Username cleanup complete - usernames are primarily generated from profile data');

    } catch (error) {
      console.error('❌ Error cleaning up old username references:', error);
      // Don't throw - this is a background operation that shouldn't fail the main profile update
    }
  }

  /**
   * Update user information in all watchlists where they are creator or collaborator
   */
  private async updateUserInfoInWatchlists(userId: string, userInfo: { displayName?: string; photoURL?: string }): Promise<void> {
    try {
      console.log('🔄 Updating user info in watchlists for user:', userId, userInfo);

      const [creatorWatchlists, collaboratorWatchlists] = await Promise.all([
        unifiedCollection('watchlists').where('createdBy', '==', userId).getDocs(),
        unifiedCollection('watchlists').where('collaborators', 'array-contains', userId).getDocs(),
      ]);

      const updates: Promise<void>[] = [];

      for (const d of creatorWatchlists) {
        const updateData: any = { updatedAt: new Date().toISOString() };
        if (userInfo.displayName !== undefined) updateData.createdByName = userInfo.displayName;
        if (userInfo.photoURL !== undefined) updateData.createdByAvatar = userInfo.photoURL;
        console.log('📝 Updating creator info in watchlist:', d.data().name);
        updates.push(unifiedUpdateDoc('watchlists', d.id, updateData));
      }

      for (const d of collaboratorWatchlists) {
        const watchlistData = d.data();
        const collaboratorProfiles = watchlistData.collaboratorProfiles || [];
        const updatedProfiles = collaboratorProfiles.map((profile: any) => {
          if (profile.uid !== userId) return profile;
          const updated = { ...profile };
          if (userInfo.displayName !== undefined) updated.name = userInfo.displayName;
          if (userInfo.photoURL !== undefined) updated.avatar = userInfo.photoURL;
          return updated;
        });
        console.log('📝 Updating collaborator info in watchlist:', watchlistData.name);
        updates.push(unifiedUpdateDoc('watchlists', d.id, {
          collaboratorProfiles: updatedProfiles,
          updatedAt: new Date().toISOString(),
        }));
      }

      await Promise.all(updates);
      console.log('✅ Successfully updated user info in all related watchlists');

    } catch (error) {
      console.error('❌ Error updating user info in watchlists:', error);
      // Don't throw - this is a background operation that shouldn't fail the main profile update
    }
  }

  // createUserPreferences - REMOVED: Now available in shared/authService.ts (~34 lines removed)
  // Temporary delegation method for backward compatibility
  async createUserPreferences(user: User, preferences: Partial<UserPreferences> = {}): Promise<void> {
    return await authService.createUserPreferences(user, preferences);
  }

  // updateUserPreferences - REMOVED: Now available in shared/authService.ts (~5 lines removed)

  // deleteUserPreferences - REMOVED: Now available in shared/authService.ts (~3 lines removed)

  // Activity Management
  async createUserActivity(uid: string, activityData: Omit<UserActivity, 'id' | 'uid' | 'createdAt'>): Promise<void> {
    const activity: Omit<UserActivity, 'id'> = {
      uid: uid,
      ...activityData,
      createdAt: this.isNative
        ? nativeFirebase.getServerTimestamp() as unknown as Timestamp
        : serverTimestamp() as Timestamp,
    };

    await unifiedAddDoc('userActivity', activity);
  }

  async getUserActivity(uid: string, limitCount: number = 20): Promise<UserActivity[]> {
    // Use native Firebase on native platforms
    if (this.isNative) {
      const results = await nativeFirebase.queryDocuments<UserActivity>(
        'userActivity',
        [
          { field: 'uid', operator: '==', value: uid },
          { field: 'isPrivate', operator: '==', value: false }
        ],
        {
          orderByField: 'createdAt',
          orderDirection: 'desc',
          limitCount: limitCount
        }
      );
      return results;
    }

    // Web SDK path
    const activitiesRef = collection(getDb(), 'userActivity');
    const q = query(
      activitiesRef,
      where('uid', '==', uid),
      where('isPrivate', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as UserActivity[];
  }

  async getFollowingActivity(followingUids: string[], limitCount: number = 50): Promise<UserActivity[]> {
    if (followingUids.length === 0) return [];

    // Use native Firebase on native platforms
    if (this.isNative) {
      const results = await nativeFirebase.queryDocuments<UserActivity>(
        'userActivity',
        [
          { field: 'uid', operator: 'in', value: followingUids.slice(0, 10) },
          { field: 'isPrivate', operator: '==', value: false }
        ],
        {
          orderByField: 'createdAt',
          orderDirection: 'desc',
          limitCount: limitCount
        }
      );
      return results;
    }

    // Web SDK path
    const activitiesRef = collection(getDb(), 'userActivity');
    const q = query(
      activitiesRef,
      where('uid', 'in', followingUids.slice(0, 10)), // Firestore 'in' query limit is 10
      where('isPrivate', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as UserActivity[];
  }

  // followUser - REMOVED: Now available in shared/socialService.ts (~90 lines removed)

  // unfollowUser - REMOVED: Now available in shared/socialService.ts (~68 lines removed)

  // isFollowing - REMOVED: Now available in shared/socialService.ts
  // Temporary delegation method for backward compatibility
  async isFollowing(followerUid: string, followingUid: string): Promise<boolean> {
    return await socialService.isFollowing(followerUid, followingUid);
  }

  // getFollowers - REMOVED: Now available in shared/socialService.ts (~6 lines removed)
  // Temporary delegation method for backward compatibility
  async getFollowers(uid: string): Promise<string[]> {
    return await socialService.getFollowers(uid);
  }

  // getFollowing - REMOVED: Now available in shared/socialService.ts (~6 lines removed)
  // Temporary delegation method for backward compatibility
  async getFollowing(uid: string): Promise<string[]> {
    return await socialService.getFollowing(uid);
  }

  /**
   * Get followers list with privacy filtering
   */
  async getFollowersForProfile(targetUserId: string, viewerUserId?: string): Promise<string[]> {
    const targetProfile = await getCachedUserProfile(targetUserId);
    if (!targetProfile) return [];

    if (targetProfile.privacy.profileVisibility !== 'private') {
      return this.getFollowers(targetUserId);
    }

    // Private profile: allow owner or approved followers
    if (viewerUserId === targetUserId) return this.getFollowers(targetUserId);
    if (!viewerUserId) return [];
    const { unifiedGetDoc } = await import('@watchlist/shared');
    const followDoc = await unifiedGetDoc('userFollowing', `${viewerUserId}_${targetUserId}`);
    return followDoc.exists() ? this.getFollowers(targetUserId) : [];
  }

  /**
   * Get following list with privacy filtering
   */
  async getFollowingForProfile(targetUserId: string, viewerUserId?: string): Promise<string[]> {
    const targetProfile = await getCachedUserProfile(targetUserId);
    if (!targetProfile) return [];

    if (targetProfile.privacy.profileVisibility !== 'private') {
      return this.getFollowing(targetUserId);
    }

    // Private profile: allow owner or approved followers
    if (viewerUserId === targetUserId) return this.getFollowing(targetUserId);
    if (!viewerUserId) return [];
    const { unifiedGetDoc } = await import('@watchlist/shared');
    const followDoc = await unifiedGetDoc('userFollowing', `${viewerUserId}_${targetUserId}`);
    return followDoc.exists() ? this.getFollowing(targetUserId) : [];
  }

  // likeWatchlist - REMOVED: Now available in shared/socialService.ts (~97 lines removed)

  // unlikeWatchlist - REMOVED: Now available in shared/socialService.ts (~42 lines removed)

  // Get real-time like count for a watchlist
  async getWatchlistLikeCount(watchlistId: string): Promise<number> {
    try {
      // Use native Firebase on native platforms
      if (this.isNative) {
        const watchlistData = await nativeFirebase.getDocument<UserWatchlist>('watchlists', watchlistId);
        return watchlistData?.followerCount || 0;
      }

      // Web SDK path
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (watchlistDoc.exists()) {
        const watchlistData = watchlistDoc.data() as UserWatchlist;
        return watchlistData.followerCount || 0;
      }

      return 0;
    } catch (error) {
      console.error('Error getting watchlist like count:', error);
      return 0;
    }
  }

  // Calculate and sync user's total likes received from all their watchlists
  async syncUserLikesReceived(userUid: string): Promise<void> {
    try {
      const watchlistDocs = await unifiedCollection('watchlists').where('createdBy', '==', userUid).getDocs();
      let totalLikes = 0;
      watchlistDocs.forEach(d => {
        const watchlist = d.data() as UserWatchlist;
        totalLikes += watchlist.followerCount || 0;
      });

      await unifiedUpdateDoc('userProfiles', userUid, {
        'stats.likesReceived': totalLikes,
        updatedAt: new Date().toISOString(),
      });

      console.log(`Synced user ${userUid} likes: ${totalLikes} total likes from ${watchlistDocs.length} watchlists`);
    } catch (error) {
      console.error('Error syncing user likes received:', error);
      throw error;
    }
  }

  // Get user's current total likes received (calculated from all watchlists)
  async getUserTotalLikesReceived(userUid: string): Promise<number> {
    try {
      const watchlistDocs = await unifiedCollection('watchlists').where('createdBy', '==', userUid).getDocs();
      let totalLikes = 0;
      watchlistDocs.forEach(d => {
        const watchlist = d.data() as UserWatchlist;
        totalLikes += watchlist.followerCount || 0;
      });
      return totalLikes;
    } catch (error) {
      console.error('Error calculating user total likes received:', error);
      return 0;
    }
  }

  // isLikingWatchlist - REMOVED: Now available in shared/socialService.ts
  // Temporary delegation method for backward compatibility
  async isLikingWatchlist(userUid: string, watchlistId: string): Promise<boolean> {
    return await socialService.isLikingWatchlist(userUid, watchlistId);
  }

  // getLikedWatchlists - REMOVED: Now available in shared/socialService.ts (~26 lines removed)
  // Temporary delegation method for backward compatibility
  async getLikedWatchlists(userUid: string): Promise<UserWatchlist[]> {
    return await socialService.getLikedWatchlists(userUid);
  }

  async getWatchlistLikers(watchlistId: string): Promise<string[]> {
    const docs = await unifiedCollection('watchlistLikes')
      .where('watchlistId', '==', watchlistId)
      .orderBy('createdAt', 'desc')
      .limit(QUERY_LIMITS.WATCHLIST_LIKES)
      .getDocs();

    return docs.map((d) => (d.data() as { userUid: string }).userUid);
  }

  /**
   * Get watchlist likers with privacy filtering
   */
  async getWatchlistLikersForProfile(watchlistId: string, viewerUserId?: string): Promise<string[]> {
    try {
      // Get all likers first
      const allLikers = await this.getWatchlistLikers(watchlistId);

      if (!viewerUserId) {
        // For non-authenticated users, filter out private profiles
        const publicLikers: string[] = [];
        for (const likerId of allLikers) {
          const likerProfile = await getCachedUserProfile(likerId);
          if (likerProfile?.privacy.profileVisibility === 'public') {
            publicLikers.push(likerId);
          }
        }
        return publicLikers;
      }

      // For authenticated users, filter based on privacy settings
      const visibleLikers: string[] = [];
      for (const likerId of allLikers) {
        const likerProfile = await getCachedUserProfile(likerId);
        if (!likerProfile) continue;

        // Always include the viewer themselves
        if (likerId === viewerUserId) {
          visibleLikers.push(likerId);
          continue;
        }

        // Include public profiles
        if (likerProfile.privacy.profileVisibility === 'public') {
          visibleLikers.push(likerId);
          continue;
        }

        // Private profiles are only visible to themselves (already handled above)
        // Note: In future, we could add friend relationships here
      }

      return visibleLikers;
    } catch (error) {
      console.error('Error getting privacy-filtered watchlist likers:', error);
      return [];
    }
  }

  // Sync like count for a specific watchlist to match actual likes
  async syncWatchlistLikeCount(watchlistId: string): Promise<{ before: number; after: number; fixed: boolean }> {
    try {
      console.log('🔧 Syncing like count for watchlist:', watchlistId);

      // Get current watchlist data
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const watchlistData = watchlistDoc.data() as UserWatchlist;
      const currentCount = watchlistData.followerCount || 0;

      // Count actual likes
      const actualCount = (await this.getWatchlistLikers(watchlistId)).length;

      console.log(`📊 Like count sync for "${watchlistData.name}":`, {
        current: currentCount,
        actual: actualCount,
        needsUpdate: currentCount !== actualCount
      });

      // Update if counts don't match
      if (currentCount !== actualCount) {
        await unifiedUpdateDoc('watchlists', watchlistId, {
          followerCount: actualCount,
          updatedAt: serverTimestamp()
        });

        console.log(`✅ Updated like count for "${watchlistData.name}": ${currentCount} → ${actualCount}`);

        return {
          before: currentCount,
          after: actualCount,
          fixed: true
        };
      }

      return {
        before: currentCount,
        after: actualCount,
        fixed: false
      };

    } catch (error) {
      console.error('❌ Error syncing like count:', error);
      throw error;
    }
  }

  // Get all users who liked any of the user's watchlists
  async getUsersWhoLikedWatchlists(userUid: string): Promise<string[]> {
    try {
      const watchlistDocs = await unifiedCollection('watchlists').where('createdBy', '==', userUid).getDocs();

      if (watchlistDocs.length === 0) {
        return [];
      }

      const watchlistIds = watchlistDocs.map(d => d.id);

      // Get all users who liked these watchlists
      const allLikers = new Set<string>();

      for (const watchlistId of watchlistIds) {
        const likers = await this.getWatchlistLikers(watchlistId);
        likers.forEach(likerId => allLikers.add(likerId));
      }

      return Array.from(allLikers);
    } catch (error) {
      console.error('Error getting users who liked watchlists:', error);
      return [];
    }
  }

  // === NOTIFICATION SYSTEM ===

  // createNotification - REMOVED: Now available in shared/socialService.ts
  // Temporary delegation method for backward compatibility
  async createNotification(notification: {
    recipientId: string;
    type: 'follow' | 'started_following' | 'collaboration' | 'collaboration_invitation' | 'collaborator_added' | 'like_watchlist' | 'share_watchlist' | 'share_movie' | 'share_profile' | 'share_person';
    actorId: string;
    actorName: string;
    actorAvatar?: string;
    entityId?: string; // watchlist ID, review ID, etc.
    entityName?: string; // watchlist name, movie title, etc.
    message: string;
    metadata?: Record<string, any>;
  }): Promise<string> {
    return await socialService.createNotification(notification);
  }

  // Smart deduplication logic for state-change notifications
  private async shouldSkipDuplicateNotification(notification: {
    recipientId: string;
    type: string;
    actorId: string;
    entityId?: string;
  }): Promise<boolean> {
    try {
      // Only apply deduplication to like notifications
      // Follow notifications should always be allowed (cleanup handles duplicates)
      const stateChangeTypes = ['like_watchlist'];
      if (!stateChangeTypes.includes(notification.type)) {
        console.log(`✅ Allowing notification type: ${notification.type} (not in state-change types)`);
        return false;
      }

      // For like_watchlist notifications, check for recent duplicates
      if (notification.type === 'like_watchlist') {
        // Look for recent like notifications for the same watchlist within 1 hour
        const oneHourAgo = new Date();
        oneHourAgo.setHours(oneHourAgo.getHours() - 1);

        const recentLikeDocs = await unifiedCollection('notifications')
          .where('recipientId', '==', notification.recipientId)
          .where('actorId', '==', notification.actorId)
          .where('type', '==', 'like_watchlist')
          .where('entityId', '==', notification.entityId)
          .getDocs();

        const oneHourAgoIso = oneHourAgo.toISOString();
        const hasRecent = recentLikeDocs.some(d => {
          const createdAt = d.data().createdAt;
          return typeof createdAt === 'string' ? createdAt > oneHourAgoIso : true;
        });

        if (hasRecent) {
          console.log('Found duplicate like_watchlist notification within 1 hour, skipping');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking for duplicate notifications:', error);
      // If there's an error, allow the notification to be created
      return false;
    }
  }

  // Generate bundle ID for grouping similar notifications
  private generateBundleId(type: string, actorId?: string, entityId?: string): string {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

    // Don't bundle 'started_following' notifications - make each one unique
    if (type === 'started_following') {
      const timestamp = Date.now();
      return `${type}_${entityId || actorId || 'general'}_${today}_${timestamp}`;
    }

    return `${type}_${entityId || actorId || 'general'}_${today}`;
  }

  // Update notification bundle for count tracking
  async updateNotificationBundle(bundleId: string, recipientId: string): Promise<void> {
    try {
      // Get existing count or default to 0, then increment
      const existingDoc = await unifiedGetDoc('notificationBundles', `${recipientId}_${bundleId}`);
      const currentCount = existingDoc.exists() ? (existingDoc.data().count || 0) : 0;

      await unifiedSetDoc('notificationBundles', `${recipientId}_${bundleId}`, {
        bundleId,
        recipientId,
        count: currentCount + 1,
        lastUpdated: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      console.error('Error updating notification bundle:', error);
    }
  }

  // Get user notifications with bundling
  async getUserNotifications(userUid: string, limitCount: number = 50): Promise<any[]> {
    try {
      console.log('📱 getUserNotifications: Fetching for user:', userUid, 'limit:', limitCount);

      // Use unified collection for cross-platform support (native + web)
      // Query by 'userId' and 'timestamp' to match Firebase Functions notification format
      const querySnapshot = await unifiedCollection('notifications')
        .where('userId', '==', userUid)
        .orderBy('timestamp', 'desc')
        .limit(limitCount || QUERY_LIMITS.NOTIFICATIONS)
        .getDocs();

      console.log('📱 getUserNotifications: Got', querySnapshot.length, 'documents');

      // Convert to array and sort by timestamp in memory
      const allNotifications = querySnapshot.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by timestamp descending (most recent first) - handle native timestamp format
      allNotifications.sort((a: any, b: any) => {
        const aTime = this.getTimestampValue(a.timestamp);
        const bTime = this.getTimestampValue(b.timestamp);
        return bTime - aTime;
      });

      // Apply limit
      const limitedNotifications = allNotifications.slice(0, limitCount);

      console.log('📱 Raw notifications fetched:', limitedNotifications.length);

      // Bundle the notifications
      const bundledNotifications = await this.bundleNotifications(limitedNotifications);

      console.log('📱 Bundled notifications:', bundledNotifications.length);
      return bundledNotifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Return empty array instead of throwing to prevent app crashes
      return [];
    }
  }

  // Helper to get timestamp value from various formats
  private getTimestampValue(timestamp: any): number {
    if (!timestamp) return 0;
    // Native adapter: {seconds, nanoseconds}
    if (typeof timestamp === 'object' && 'seconds' in timestamp) {
      return timestamp.seconds * 1000;
    }
    // Web SDK Timestamp with toDate method
    if (typeof timestamp?.toDate === 'function') {
      return timestamp.toDate().getTime();
    }
    // String format
    if (typeof timestamp === 'string') {
      const match = timestamp.match(/Timestamp\(seconds=(\d+),\s*nanoseconds=\d+\)/);
      if (match) {
        return parseInt(match[1], 10) * 1000;
      }
      return new Date(timestamp).getTime();
    }
    // Number
    if (typeof timestamp === 'number') {
      return timestamp;
    }
    return 0;
  }

  // Bundle similar notifications together
  private async bundleNotifications(notifications: any[]): Promise<any[]> {
    const bundles = new Map<string, any[]>();
    const unbundled: any[] = [];

    // Group notifications by bundleId - notifications without bundleId are treated individually
    notifications.forEach(notification => {
      const bundleId = notification.bundleId;
      if (!bundleId) {
        // No bundleId - treat as individual notification
        unbundled.push(notification);
        return;
      }
      if (!bundles.has(bundleId)) {
        bundles.set(bundleId, []);
      }
      bundles.get(bundleId)!.push(notification);
    });

    const bundledResults: any[] = [...unbundled];

    // Convert Map entries to array for iteration
    for (const [bundleId, bundleNotifications] of Array.from(bundles.entries())) {
      if (bundleNotifications.length === 1) {
        // Single notification - no bundling needed
        bundledResults.push(bundleNotifications[0]);
      } else {
        // Multiple notifications - create bundled notification
        const latest = bundleNotifications[0]; // Already sorted by createdAt desc
        const count = bundleNotifications.length;
        const actors = Array.from(new Set(bundleNotifications.map((n: any) => n.actorName)));

        // Use local count from fetched notifications
        // Note: notificationBundles collection is not accessible (missing security rules)
        // so we use the local count which is accurate enough
        let totalCount = count;

        bundledResults.push({
          ...latest,
          isBundled: true,
          bundleCount: totalCount,
          bundleActors: actors.slice(0, 3), // Show max 3 actor names
          bundleMessage: this.generateBundledMessage(latest.type, actors, totalCount, latest.entityName),
          bundledNotifications: bundleNotifications,
        });
      }
    }

    return bundledResults.sort((a, b) => {
      const aTime = this.getTimestampValue(a.createdAt || a.timestamp);
      const bTime = this.getTimestampValue(b.createdAt || b.timestamp);
      return bTime - aTime;
    });
  }

  // Generate bundled message based on type and count
  private generateBundledMessage(type: string, actors: string[], count: number, entityName?: string): string {
    const firstActor = actors[0];
    const remainingCount = count - 1;

    switch (type) {
      case 'follow':
        if (count === 2) {
          return `${firstActor} and 1 other started following you`;
        }
        return `${firstActor} and ${remainingCount} others started following you`;

      case 'started_following':
        // Don't bundle 'started_following' notifications - they should remain individual
        return `You started following ${entityName || 'someone'}`;

      case 'like_watchlist':
        if (count === 2) {
          return `${firstActor} and 1 other liked your watchlist${entityName ? ` "${entityName}"` : ''}`;
        }
        return `${firstActor} and ${remainingCount} others liked your watchlist${entityName ? ` "${entityName}"` : ''}`;

      case 'collaboration':
        if (count === 2) {
          return `${firstActor} and 1 other invited you to collaborate`;
        }
        return `${firstActor} and ${remainingCount} others invited you to collaborate`;

      case 'collaboration_invitation':
        if (count === 2) {
          return `${firstActor} and 1 other invited you to collaborate on "${entityName}"`;
        }
        return `${firstActor} and ${remainingCount} others invited you to collaborate on watchlists`;

      case 'collaborator_added':
        if (count === 2) {
          return `${firstActor} and 1 other joined your watchlist "${entityName}" as collaborators`;
        }
        return `${firstActor} and ${remainingCount} others joined your watchlists as collaborators`;

      case 'share_movie':
        if (count === 2) {
          return `${firstActor} and 1 other shared "${entityName}" with you`;
        }
        return `${firstActor} and ${remainingCount} others shared "${entityName}" with you`;

      case 'share_watchlist':
        if (count === 2) {
          return `${firstActor} and 1 other shared "${entityName}" watchlist with you`;
        }
        return `${firstActor} and ${remainingCount} others shared "${entityName}" watchlist with you`;

      default:
        return `${firstActor} and ${remainingCount} others interacted with you`;
    }
  }

  // markNotificationAsRead - REMOVED: Now available in shared/socialService.ts
  // Temporary delegation method for backward compatibility
  async markNotificationAsRead(notificationId: string): Promise<void> {
    return await socialService.markNotificationAsRead(notificationId);
  }

  // markAllNotificationsAsRead - REMOVED: Now available in shared/socialService.ts
  // Temporary delegation method for backward compatibility
  async markAllNotificationsAsRead(userUid: string): Promise<void> {
    return await socialService.markAllNotificationsAsRead(userUid);
  }

  // getUnreadNotificationCount - REMOVED: Now available in shared/socialService.ts
  // Temporary delegation method for backward compatibility
  async getUnreadNotificationCount(userUid: string): Promise<number> {
    return await socialService.getUnreadNotificationCount(userUid);
  }

  // deleteNotification - REMOVED: Now available in shared/socialService.ts
  // Temporary delegation method for backward compatibility
  async deleteNotification(notificationId: string): Promise<void> {
    return await socialService.deleteNotification(notificationId);
  }

  // Clean up opposite notifications (like when user unlikes after liking, or unfollows after following)
  private async cleanupOppositeNotifications(
    actorId: string,
    recipientId: string,
    notificationType: string,
    entityId?: string
  ): Promise<void> {
    try {
      console.log('🧹 Cleaning up opposite notifications:', { actorId, recipientId, notificationType, entityId });

      // For follow notifications, look for ALL follow notifications from this actor to this recipient
      // (no time limit since follow/unfollow should always clean up the notification)
      if (notificationType === 'follow') {
        const followDocs = await unifiedCollection('notifications')
          .where('recipientId', '==', recipientId)
          .where('actorId', '==', actorId)
          .where('type', '==', 'follow')
          .getDocs();

        console.log(`🧹 Found ${followDocs.length} follow notifications to clean up`);
        await Promise.all(followDocs.map(d => {
          console.log('🗑️ Removing follow notification:', d.id);
          return unifiedDeleteDoc('notifications', d.id);
        }));
        console.log('✅ Follow notifications cleanup completed');
        return;
      }

      // For other notification types, use time-based cleanup
      const sevenDaysAgoIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const recentDocs = await unifiedCollection('notifications')
        .where('recipientId', '==', recipientId)
        .where('actorId', '==', actorId)
        .where('type', '==', notificationType)
        .getDocs();

      for (const d of recentDocs) {
        const notification = d.data();
        const createdAt = typeof notification.createdAt === 'string'
          ? notification.createdAt
          : new Date(0).toISOString();

        if (createdAt < sevenDaysAgoIso) continue;

        if (notificationType === 'like_watchlist' && notification.entityId === entityId) {
          console.log('🗑️ Removing duplicate like notification:', d.id);
          await unifiedDeleteDoc('notifications', d.id);
        }
      }
    } catch (error) {
      console.error('Error cleaning up opposite notifications:', error);
      // Don't throw - this is cleanup, not critical
    }
  }

  // Helper methods to create specific notification types
  async createFollowNotification(followerId: string, followedId: string): Promise<string> {
    console.log('🔔 Creating follow notification:', { followerId, followedId });

    const followerProfile = await getCachedUserProfile(followerId);
    if (!followerProfile) {
      console.warn('❌ Cannot create follow notification: follower profile not found');
      return '';
    }

    console.log('✅ Follower profile found, creating notification');
    const notificationId = await this.createNotification({
      recipientId: followedId,
      type: 'follow',
      actorId: followerId,
      actorName: followerProfile.displayName || 'Someone',
      actorAvatar: followerProfile.photoURL,
      message: `${followerProfile.displayName || 'Someone'} started following you`,
    });

    console.log('📬 Follow notification created with ID:', notificationId);
    return notificationId;
  }

  async createStartedFollowingNotification(followerId: string, followedId: string): Promise<string> {
    const followedProfile = await getCachedUserProfile(followedId);
    if (!followedProfile) return '';

    return this.createNotification({
      recipientId: followerId, // Notify the follower (current user)
      type: 'started_following',
      actorId: followerId, // The follower is the actor
      actorName: followedProfile.displayName || 'Someone',
      actorAvatar: followedProfile.photoURL,
      entityId: followedId, // The followed user's ID
      entityName: followedProfile.displayName || 'Someone',
      message: `You started following ${followedProfile.displayName || 'Someone'}`,
    });
  }

  async createWatchlistLikeNotification(likerId: string, watchlistId: string, watchlistOwnerId: string): Promise<string> {
    if (likerId === watchlistOwnerId) return ''; // Don't notify self

    const [likerProfile, watchlists] = await Promise.all([
      getCachedUserProfile(likerId),
      watchlistService.getUserWatchlists(watchlistOwnerId)
    ]);

    if (!likerProfile) return '';

    // Find the specific watchlist
    const watchlist = watchlists.find(w => w.id === watchlistId);

    return this.createNotification({
      recipientId: watchlistOwnerId,
      type: 'like_watchlist',
      actorId: likerId,
      actorName: likerProfile.displayName || 'Someone',
      actorAvatar: likerProfile.photoURL,
      entityId: watchlistId,
      entityName: watchlist?.name || 'your watchlist',
      message: `${likerProfile.displayName || 'Someone'} liked your watchlist "${watchlist?.name || 'Untitled'}"`,
    });
  }

  // Create sharing notifications for movies and watchlists
  async createMovieSharedNotification(
    sharerId: string,
    recipientId: string,
    movieId: string,
    movieTitle: string,
    shareMethod: 'link' | 'direct' | 'email' | 'social' = 'direct',
    moviePoster?: string,
    movieYear?: string
  ): Promise<string> {
    try {
      const sharerProfile = await getCachedUserProfile(sharerId);
      if (!sharerProfile) throw new Error('Sharer profile not found');

      const message = `${sharerProfile.displayName} shared "${movieTitle}" with you`;

      return await this.createNotification({
        recipientId,
        type: 'share_movie',
        actorId: sharerId,
        actorName: sharerProfile.displayName,
        actorAvatar: sharerProfile.photoURL,
        entityId: movieId,
        entityName: movieTitle,
        message,
        metadata: notificationMetadataFrom({
          shareMethod,
          moviePoster,
          movieYear,
        }),
      });
    } catch (error) {
      console.error('Error creating movie shared notification:', error);
      throw error;
    }
  }

  async createWatchlistSharedNotification(
    sharerId: string,
    recipientId: string,
    watchlistId: string,
    watchlistName: string,
    shareMethod: 'link' | 'direct' | 'email' | 'social' = 'direct',
    watchlistMovieCount?: number,
    watchlistPosters?: string[]
  ): Promise<string> {
    try {
      const sharerProfile = await getCachedUserProfile(sharerId);
      if (!sharerProfile) throw new Error('Sharer profile not found');

      const message = `${sharerProfile.displayName} shared "${watchlistName}" watchlist with you`;

      return await this.createNotification({
        recipientId,
        type: 'share_watchlist',
        actorId: sharerId,
        actorName: sharerProfile.displayName,
        actorAvatar: sharerProfile.photoURL,
        entityId: watchlistId,
        entityName: watchlistName,
        message,
        metadata: notificationMetadataFrom({
          shareMethod,
          watchlistMovieCount,
          watchlistPosters,
        }),
      });
    } catch (error) {
      console.error('Error creating watchlist shared notification:', error);
      throw error;
    }
  }

  async createCollaborationInvitationNotification(
    inviterId: string,
    recipientId: string,
    watchlistId: string,
    watchlistName: string,
    watchlistMovieCount?: number,
    watchlistPosters?: string[]
  ): Promise<string> {
    try {
      const inviterProfile = await getCachedUserProfile(inviterId);
      if (!inviterProfile) throw new Error('Inviter profile not found');

      const message = `${inviterProfile.displayName} invited you to collaborate on "${watchlistName}"`;

      return await this.createNotification({
        recipientId,
        type: 'collaboration_invitation',
        actorId: inviterId,
        actorName: inviterProfile.displayName,
        actorAvatar: inviterProfile.photoURL,
        entityId: watchlistId,
        entityName: watchlistName,
        message,
        metadata: notificationMetadataFrom({
          watchlistMovieCount,
          watchlistPosters,
          invitationType: 'watchlist_collaboration',
        }),
      });
    } catch (error) {
      console.error('Error creating collaboration invitation notification:', error);
      throw error;
    }
  }

  async createCollaboratorAddedNotification(
    watchlistOwnerId: string,
    newCollaboratorId: string,
    watchlistId: string,
    watchlistName: string,
    watchlistMovieCount?: number,
    watchlistPosters?: string[]
  ): Promise<string> {
    try {
      const collaboratorProfile = await getCachedUserProfile(newCollaboratorId);
      if (!collaboratorProfile) throw new Error('Collaborator profile not found');

      const message = `${collaboratorProfile.displayName} is now a collaborator in your watchlist "${watchlistName}"`;

      return await this.createNotification({
        recipientId: watchlistOwnerId,
        type: 'collaborator_added',
        actorId: newCollaboratorId,
        actorName: collaboratorProfile.displayName,
        actorAvatar: collaboratorProfile.photoURL,
        entityId: watchlistId,
        entityName: watchlistName,
        message,
        metadata: notificationMetadataFrom({
          watchlistMovieCount,
          watchlistPosters,
          action: 'collaborator_joined',
        }),
      });
    } catch (error) {
      console.error('Error creating collaborator added notification:', error);
      throw error;
    }
  }

  async createProfileSharedNotification(
    sharerId: string,
    recipientId: string,
    sharedProfileId: string,
    sharedProfileName: string,
    shareMethod: 'link' | 'direct' | 'email' | 'social' = 'direct',
    sharedProfilePhoto?: string,
    sharedProfileUsername?: string
  ): Promise<string> {
    try {
      const sharerProfile = await getCachedUserProfile(sharerId);
      if (!sharerProfile) throw new Error('Sharer profile not found');

      const message = `${sharerProfile.displayName} shared ${sharedProfileName}'s profile with you`;

      return await this.createNotification({
        recipientId,
        type: 'share_profile',
        actorId: sharerId,
        actorName: sharerProfile.displayName,
        actorAvatar: sharerProfile.photoURL,
        entityId: sharedProfileId,
        entityName: sharedProfileName,
        message,
        metadata: notificationMetadataFrom({
          shareMethod,
          sharedProfilePhoto,
          sharedProfileUsername,
        }),
      });
    } catch (error) {
      console.error('Error creating profile shared notification:', error);
      throw error;
    }
  }

  async createPersonSharedNotification(
    sharerId: string,
    recipientId: string,
    personId: string,
    personName: string,
    shareMethod: 'link' | 'direct' | 'email' | 'social' = 'direct',
    personPhoto?: string,
    personDepartment?: string
  ): Promise<string> {
    try {
      const sharerProfile = await getCachedUserProfile(sharerId);
      if (!sharerProfile) throw new Error('Sharer profile not found');

      const departmentText = personDepartment ? ` (${personDepartment})` : '';
      const message = `${sharerProfile.displayName} shared ${personName}${departmentText} with you`;

      return await this.createNotification({
        recipientId,
        type: 'share_person',
        actorId: sharerId,
        actorName: sharerProfile.displayName,
        actorAvatar: sharerProfile.photoURL,
        entityId: personId,
        entityName: personName,
        message,
        metadata: notificationMetadataFrom({
          shareMethod,
          personPhoto,
          personDepartment,
        }),
      });
    } catch (error) {
      console.error('Error creating person shared notification:', error);
      throw error;
    }
  }

  // Real-time listeners
  subscribeToUserActivity(uid: string, callback: (activities: UserActivity[]) => void): Unsubscribe {
    const activitiesRef = collection(getDb(), 'userActivity');
    const q = query(
      activitiesRef,
      where('uid', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    return onSnapshot(q, (snapshot) => {
      const activities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserActivity[];
      callback(activities);
    });
  }

  subscribeToUserWatchlists(uid: string, callback: (watchlists: UserWatchlist[]) => void): Unsubscribe {
    const watchlistsRef = collection(getDb(), 'watchlists');
    const q = query(
      watchlistsRef,
      where('uid', '==', uid),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const watchlists = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserWatchlist[];
      callback(watchlists);
    });
  }

  // Search and Discovery
  async searchUsers(searchTerm: string, limitCount: number = 10): Promise<UserProfile[]> {
    try {
      // Check cache first - exact match
      const cachedResults = userSearchCache.get(searchTerm);
      if (cachedResults) {
        return cachedResults.slice(0, limitCount).map(user => this.filterUserProfileForPrivacy(user));
      }

      // Check cache for prefix match (e.g., "john" cached, searching "john doe")
      const prefixResults = userSearchCache.getFromPrefix(searchTerm);
      if (prefixResults && prefixResults.length >= limitCount) {
        // Cache the filtered results for future use
        userSearchCache.set(searchTerm, prefixResults);
        return prefixResults.slice(0, limitCount).map(user => this.filterUserProfileForPrivacy(user));
      }

      // Fetch more users to ensure comprehensive search results
      // Since Firestore doesn't support full-text search, we need to fetch more docs
      // and filter client-side
      const FETCH_MULTIPLIER = 10; // Fetch 10x the limit to ensure we find matches
      const allUsers: UserProfile[] = [];

      // Use native Firestore on native platforms to avoid WKWebView timeout issues
      if (this.isNative) {
        console.log(`🔍 [Native] Searching users for: "${searchTerm}"`);

        // Query public users using native plugin
        const publicUsers = await nativeFirebase.queryDocuments<UserProfile>(
          'userProfiles',
          [{ field: 'privacy.profileVisibility', operator: '==', value: 'public' }],
          { limitCount: limitCount * FETCH_MULTIPLIER }
        );

        // Query private users using native plugin
        const privateUsers = await nativeFirebase.queryDocuments<UserProfile>(
          'userProfiles',
          [{ field: 'privacy.profileVisibility', operator: '==', value: 'private' }],
          { limitCount: limitCount * FETCH_MULTIPLIER }
        );

        // Combine and deduplicate
        [...publicUsers, ...privateUsers].forEach(user => {
          if (!allUsers.find(u => u.uid === user.uid)) {
            allUsers.push(user);
          }
        });

        console.log(`🔍 [Native] Fetched ${allUsers.length} total users for filtering`);
      } else {
        // Web SDK path
        const usersRef = collection(getDb(), 'userProfiles');

        const queries = [
          // Public users
          query(
            usersRef,
            where('privacy.profileVisibility', '==', 'public'),
            limit(limitCount * FETCH_MULTIPLIER)
          ),
          // Private users (still appear in search per requirements)
          query(
            usersRef,
            where('privacy.profileVisibility', '==', 'private'),
            limit(limitCount * FETCH_MULTIPLIER)
          ),
          // Users with profileVisibility field missing (newly added users - default public)
          query(
            usersRef,
            where('privacy.profileVisibility', '==', null),
            limit(limitCount * FETCH_MULTIPLIER)
          )
        ];

        const queryResults = await Promise.all(queries.map(q => getDocs(q)));

        queryResults.forEach(querySnapshot => {
          querySnapshot.docs.forEach(doc => {
            const userData = doc.data() as UserProfile;
            // Deduplicate by uid
            if (!allUsers.find(u => u.uid === userData.uid)) {
              allUsers.push(userData);
            }
          });
        });

        console.log(`🔍 [Web] Fetched ${allUsers.length} total users for filtering`);
      }

      console.log(`🔍 Search: Fetched ${allUsers.length} total users for filtering with term: "${searchTerm}"`);

      // Enhanced client-side filtering with better text matching
      const searchTermLower = searchTerm.toLowerCase().trim();
      const filteredUsers = allUsers.filter(user => {
        if (!user.displayName && !user.email && !user.username) return false;

        const displayName = (user.displayName || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const username = (user.username || '').toLowerCase();

        // Check for exact matches and partial matches
        return (
          displayName.includes(searchTermLower) ||
          email.includes(searchTermLower) ||
          username.includes(searchTermLower) ||
          // Check if search term matches start of words
          displayName.split(' ').some(word => word.startsWith(searchTermLower)) ||
          username.startsWith(searchTermLower) ||
          // Check email username part (before @)
          email.split('@')[0].includes(searchTermLower)
        );
      });

      console.log(`🔍 Search: Found ${filteredUsers.length} matching users for term: "${searchTerm}"`);

      // Sort results by relevance (exact matches first, then partial matches)
      filteredUsers.sort((a, b) => {
        const aDisplayName = (a.displayName || '').toLowerCase();
        const bDisplayName = (b.displayName || '').toLowerCase();
        const aUsername = (a.username || '').toLowerCase();
        const bUsername = (b.username || '').toLowerCase();

        // Exact display name matches first
        if (aDisplayName === searchTermLower && bDisplayName !== searchTermLower) return -1;
        if (bDisplayName === searchTermLower && aDisplayName !== searchTermLower) return 1;

        // Exact username matches next
        if (aUsername === searchTermLower && bUsername !== searchTermLower) return -1;
        if (bUsername === searchTermLower && aUsername !== searchTermLower) return 1;

        // Start-of-string matches
        if (aDisplayName.startsWith(searchTermLower) && !bDisplayName.startsWith(searchTermLower)) return -1;
        if (bDisplayName.startsWith(searchTermLower) && !aDisplayName.startsWith(searchTermLower)) return 1;

        // Sort by followers count (popularity)
        const aFollowers = a.stats?.followersCount || 0;
        const bFollowers = b.stats?.followersCount || 0;
        return bFollowers - aFollowers;
      });

      // Cache the results before returning (store unfiltered for reuse)
      userSearchCache.set(searchTerm, filteredUsers);

      // Return results with privacy-filtered data
      return filteredUsers.slice(0, limitCount).map(user => this.filterUserProfileForPrivacy(user));
    } catch (error) {
      console.error('Error searching users:', error);

      // Fallback to simple search - fetch even more users
      try {
        let users: UserProfile[] = [];

        if (this.isNative) {
          // Native fallback - fetch all users without filter
          const fallbackUsers = await nativeFirebase.queryDocuments<UserProfile>(
            'userProfiles',
            [],
            { limitCount: limitCount * 20 }
          );
          users = fallbackUsers;
        } else {
          // Web fallback
          const usersRef = collection(getDb(), 'userProfiles');
          const fallbackQuery = query(usersRef, limit(limitCount * 20));
          const querySnapshot = await getDocs(fallbackQuery);
          users = querySnapshot.docs.map(doc => doc.data() as UserProfile);
        }

        const filteredUsers = users.filter(user =>
          (user.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.username || '').toLowerCase().includes(searchTerm.toLowerCase())
        ).slice(0, limitCount);

        // Cache fallback results too
        userSearchCache.set(searchTerm, filteredUsers);

        return filteredUsers.map(user => this.filterUserProfileForPrivacy(user));
      } catch (fallbackError) {
        console.error('Fallback search also failed:', fallbackError);
        return [];
      }
    }
  }

  /**
   * Filter user profile data based on privacy settings (hides email for everyone)
   */
  private filterUserProfileForPrivacy(user: UserProfile): UserProfile {
    return {
      ...user,
      // Hide email for all users in public views (per requirements)
      email: '',
      // Keep other privacy-sensitive data based on user's settings
      // (follower/following lists will be filtered at the UI level)
    };
  }

  // getPublicWatchlists - REMOVED: Now available in shared/watchlistService.ts
  // Temporary delegation method for backward compatibility
  async getPublicWatchlists(limitCount: number = 20): Promise<UserWatchlist[]> {
    return await watchlistService.getPublicWatchlists(limitCount);
  }

  // initializeUserData - REMOVED: Simplified to use shared services directly  
  // Temporary delegation method for backward compatibility
  async initializeUserData(user: User): Promise<void> {
    // Use shared services directly
    const existingProfile = await getCachedUserProfile(user.uid);
    if (!existingProfile) {
      await authService.createUserProfile(user);
    }

    // Note: User preferences initialization has been simplified
    console.log('InitializeUserData: User initialization completed');
  }

  // deleteUserData - REMOVED: Complex function kept for now (TODO: move to shared services)
  // This function uses deleteAllUserData which has complex internal logic
  async deleteUserData(uid: string): Promise<void> {
    console.log('FirebaseService: deleteUserData called for uid:', uid);
    try {
      // Use the comprehensive deletion method
      await this.deleteAllUserData(uid);
      console.log('FirebaseService: deleteUserData completed successfully for uid:', uid);
    } catch (error) {
      console.error('FirebaseService: Error in deleteUserData for uid:', uid, error);
      throw error;
    }
  }

  // saveRecentSearch - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async saveRecentSearch(
    user: User,
    searchQuery: string,
    searchType: 'movie' | 'person' | 'user',
    resultCount: number
  ): Promise<void> {
    return await dataService.saveRecentSearch(user, searchQuery, searchType, resultCount);
  }

  // getRecentSearches - REMOVED: Now available as getSearchHistory in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async getRecentSearches(user: User): Promise<RecentSearch[]> {
    return await dataService.getSearchHistory(user);
  }

  // getPopularSearches - REMOVED: Function deprecated (no equivalent in shared services)
  // Temporary delegation method for backward compatibility - returns empty array
  async getPopularSearches(user: User): Promise<string[]> {
    console.warn('getPopularSearches is deprecated - returning empty array');
    return [];
  }

  // clearRecentSearches - REMOVED: Now available as clearSearchHistory in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async clearRecentSearches(user: User): Promise<void> {
    return await dataService.clearSearchHistory(user);
  }

  // removeRecentSearch - REMOVED: Now available as deleteSearchFromHistory in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async removeRecentSearch(user: User, searchId: string): Promise<void> {
    // Note: dataService.deleteSearchFromHistory takes searchQuery, not searchId
    // For backward compatibility, we'll need to get the search first or deprecate this function
    console.warn('removeRecentSearch is deprecated - use deleteSearchFromHistory with search query instead');
    return;
  }

  async getSearchStats(user: User): Promise<UserSearchHistory['searchStats'] | null> {
    try {
      const historyDoc = await unifiedGetDoc('userSearchHistory', user.uid);

      if (historyDoc.exists()) {
        const history = historyDoc.data() as UserSearchHistory;
        return history.searchStats || null;
      }

      return null;
    } catch (error) {
      console.error('Error getting search stats:', error);
      return null;
    }
  }

  // addWatchlistActivity - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async addWatchlistActivity(user: User, movieId: string, movieTitle: string, moviePoster?: string): Promise<void> {
    return await dataService.createUserActivity(user, 'movie_added_to_list', {
      movieId,
      movieTitle,
      moviePoster
    });
  }

  // addWatchedActivity - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async addWatchedActivity(user: User, movieId: string, movieTitle: string, rating?: number, moviePoster?: string): Promise<void> {
    return await dataService.createUserActivity(user, 'movie_watched', {
      movieId,
      movieTitle,
      moviePoster,
      rating
    });
  }

  // addWatchlistLikedActivity - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async addWatchlistLikedActivity(user: User, watchlistId: string, watchlistName: string, watchlistMovieCount: number, watchlistPosters?: string[]): Promise<void> {
    return await dataService.createUserActivity(user, 'watchlist_liked', {
      listId: watchlistId,
      listName: watchlistName,
      watchlistMovieCount,
      watchlistPosters: watchlistPosters?.slice(0, 3) || []
    });
  }

  // addUserFollowedActivity - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility  
  async addUserFollowedActivity(user: User, targetUserId: string, targetUserName: string, targetUserAvatar?: string, targetUserStats?: { followers: number; watchlists: number }): Promise<void> {
    return await dataService.createUserActivity(user, 'user_followed', {
      targetUserId,
      targetUserName,
      targetUserAvatar,
      targetUserStats
    });
  }

  // addWatchlistSharedActivity - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async addWatchlistSharedActivity(user: User, watchlistId: string, watchlistName: string, shareMethod: 'link' | 'email' | 'social', watchlistMovieCount?: number, watchlistPosters?: string[]): Promise<void> {
    return await dataService.createUserActivity(user, 'watchlist_shared', {
      listId: watchlistId,
      listName: watchlistName,
      shareMethod,
      watchlistMovieCount: watchlistMovieCount || 0,
      watchlistPosters: watchlistPosters?.slice(0, 3) || []
    });
  }

  // addCollaboratorAddedActivity - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async addCollaboratorAddedActivity(user: User, watchlistId: string, watchlistName: string, collaboratorId: string, collaboratorName: string, collaboratorAvatar?: string): Promise<void> {
    return await dataService.createUserActivity(user, 'collaborator_added', {
      listId: watchlistId,
      listName: watchlistName,
      collaboratorId,
      collaboratorName,
      collaboratorAvatar
    });
  }

  // addWatchlistCreatedActivity - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async addWatchlistCreatedActivity(user: User, watchlistId: string, watchlistName: string, watchlistDescription?: string, watchlistIsPrivate?: boolean): Promise<void> {
    return await dataService.createUserActivity(user, 'watchlist_created', {
      listId: watchlistId,
      listName: watchlistName,
      watchlistDescription,
      watchlistIsPrivate: watchlistIsPrivate || false,
      watchlistMovieCount: 0
    });
  }

  /**
   * Delete user account and all associated data
   * TODO: Replace with shared/authService.ts (complex function with many helpers)
   */
  async deleteUserAccount(user: User, password?: string): Promise<void> {
    const userId = user.uid;

    try {
      console.log('FirebaseService: Starting account deletion for user:', userId);

      let currentUser = user;

      // Step 1: Re-authenticate first if password is provided
      if (password && user.email) {
        try {
          await this.reauthenticateUser(user, password);
          console.log('FirebaseService: User re-authenticated successfully');

          // Get the fresh user object after re-authentication
          const { getAuth } = await import('firebase/auth');
          const auth = getAuth();
          if (auth.currentUser) {
            currentUser = auth.currentUser;
            console.log('FirebaseService: Using fresh user object after re-auth');
          }
        } catch (reauthError: any) {
          console.error('FirebaseService: Re-authentication failed:', reauthError);
          throw reauthError;
        }
      }

      // Step 2: Delete all Firestore data FIRST (while auth token is still valid)
      // Doing this before deleteUser ensures security rules pass.
      try {
        await unifiedDeleteDoc('userProfiles', userId);
        console.log('FirebaseService: userProfiles doc deleted successfully');
      } catch (profileDeleteError) {
        console.error('FirebaseService: Failed to delete userProfiles doc:', profileDeleteError);
      }

      await this.deleteAllUserData(userId);

      // Step 3: Delete the Firebase Auth account last (auth token still valid above)
      // On native Capacitor, the web SDK's deleteUser() hangs because the session
      // is held by the native plugin, not the web SDK. Use the native plugin instead.
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.deleteUser();
        console.log('FirebaseService: Auth account deleted successfully (native)');
      } else {
        const { deleteUser } = await import('firebase/auth');
        await deleteUser(currentUser);
        console.log('FirebaseService: Auth account deleted successfully (web)');
      }

      console.log('FirebaseService: Account deletion completed successfully');
    } catch (error: any) {
      console.error('FirebaseService: Error deleting account:', error);

      // If it's a requires-recent-login error and we haven't tried re-auth yet
      if (error.code === 'auth/requires-recent-login' && !password) {
        // Re-throw with more specific information
        const enhancedError = new Error('Recent authentication required');
        (enhancedError as any).code = 'auth/requires-recent-login';
        (enhancedError as any).originalError = error;
        throw enhancedError;
      }

      throw error;
    }
  }

  /**
   * Re-authenticate user with their password
   */
  async reauthenticateUser(user: User, password: string): Promise<void> {
    try {
      if (!user.email) {
        throw new Error('User email is required for re-authentication');
      }

      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        // On native, the web SDK currentUser is null; re-auth by signing in fresh via the native plugin.
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.signInWithEmailAndPassword({ email: user.email, password });
      } else {
        const { reauthenticateWithCredential, EmailAuthProvider } = await import('firebase/auth');
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      }

      console.log('FirebaseService: User re-authenticated successfully');
    } catch (error: any) {
      console.error('FirebaseService: Re-authentication error:', error);
      throw error;
    }
  }

  /**
   * Check if user signed in with email/password (needs password for re-auth)
   */
  isPasswordProvider(user: User): boolean {
    return user.providerData?.some(provider => provider.providerId === 'password') ?? false;
  }

  /**
   * Delete all user data from Firestore collections
   */
  private async deleteAllUserData(userId: string): Promise<void> {
    try {
      console.log('FirebaseService: Deleting all data for user:', userId);

      // Single-doc collections keyed by userId
      const singleDocCollections = [
        'userPreferences',
        'userSearchHistory',
        'userRecentMovies',
        'userRecentlyAddedMovies',
        'userFavoritePeople',
      ];

      for (const collectionName of singleDocCollections) {
        console.log(`FirebaseService: Cleaning collection: ${collectionName}`);
        try {
          await unifiedDeleteDoc(collectionName, userId);
        } catch (_) {}
      }

      // Multi-doc collections queried by field
      const multiDocCollections = [
        { name: 'userMovies', field: 'uid' },
        { name: 'watchlists', field: 'createdBy' },
        { name: 'userActivity', field: 'uid' },
        { name: 'userActivities', field: 'uid' },
        { name: 'userProfiles', field: 'uid' },
        { name: 'movieReviews', field: 'uid' },
        { name: 'crewPosts', field: 'authorUid' },
        { name: 'comments', field: 'authorUid' },
        { name: 'userBlocks', field: 'blockerUid' },
        { name: 'userPushTokens', field: 'userId' },
      ];

      for (const { name: collectionName, field } of multiDocCollections) {
        console.log(`FirebaseService: Cleaning collection: ${collectionName}`);
        const docs = await unifiedCollection(collectionName).where(field, '==', userId).getDocs();
        console.log(`FirebaseService: Found ${docs.length} documents in ${collectionName}`);
        for (const d of docs) {
          try { await unifiedDeleteDoc(collectionName, d.id); } catch (_) {}
        }
      }

      // Follow relationships (follower side and following side)
      console.log('FirebaseService: Cleaning user follow relationships');
      const followerDocs = await unifiedCollection('userFollowing').where('followerUid', '==', userId).getDocs();
      for (const d of followerDocs) {
        try { await unifiedDeleteDoc('userFollowing', d.id); } catch (_) {}
      }
      const followingDocs = await unifiedCollection('userFollowing').where('followingUid', '==', userId).getDocs();
      for (const d of followingDocs) {
        try { await unifiedDeleteDoc('userFollowing', d.id); } catch (_) {}
      }

      // Watchlist likes
      console.log('FirebaseService: Cleaning watchlist likes');
      const likeDocs = await unifiedCollection('watchlistLikes').where('userUid', '==', userId).getDocs();
      console.log(`FirebaseService: Found ${likeDocs.length} watchlist likes to delete`);
      for (const d of likeDocs) {
        try { await unifiedDeleteDoc('watchlistLikes', d.id); } catch (_) {}
      }

      // Notifications
      console.log('FirebaseService: Cleaning user notifications');
      const notifDocs = await unifiedCollection('notifications').where('recipientId', '==', userId).getDocs();
      console.log(`FirebaseService: Found ${notifDocs.length} notifications to delete`);
      for (const d of notifDocs) {
        try { await unifiedDeleteDoc('notifications', d.id); } catch (_) {}
      }

      // Notification bundles — collection has missing security rules so the query
      // hangs indefinitely on iOS/Android native. Skip it to unblock account deletion.
      console.log('FirebaseService: Skipping notificationBundles (no security rules — would hang on native)');

      // Remove user from collaborators in other watchlists
      console.log('FirebaseService: Removing user from collaborators in other watchlists');
      const collaboratorWatchlists = await unifiedCollection('watchlists').where('collaborators', 'array-contains', userId).getDocs();
      for (const d of collaboratorWatchlists) {
        const data = d.data();
        const updatedCollaborators = (data.collaborators || []).filter((id: string) => id !== userId);
        try {
          await unifiedUpdateDoc('watchlists', d.id, {
            collaborators: updatedCollaborators,
            updatedAt: new Date().toISOString(),
          });
        } catch (_) {}
      }

      console.log('FirebaseService: Successfully deleted all user data');

    } catch (error) {
      console.error('FirebaseService: Error deleting user data:', error);
      throw error;
    }
  }

  /**
   * Get user data summary for deletion confirmation
   */
  async getUserDataSummary(userId: string): Promise<{
    watchlistsCount: number;
    moviesCount: number;
    reviewsCount: number;
    activitiesCount: number;
  }> {
    try {
      const [watchlists, movies, reviews, activities] = await Promise.all([
        unifiedCollection('watchlists').where('createdBy', '==', userId).getDocs(),
        unifiedCollection('userMovies').where('uid', '==', userId).getDocs(),
        unifiedCollection('movieReviews').where('uid', '==', userId).getDocs(),
        unifiedCollection('userActivity').where('uid', '==', userId).getDocs(),
      ]);

      return {
        watchlistsCount: watchlists.length,
        moviesCount: movies.length,
        reviewsCount: reviews.length,
        activitiesCount: activities.length,
      };
    } catch (error) {
      console.error('FirebaseService: Error getting user data summary:', error);
      return {
        watchlistsCount: 0,
        moviesCount: 0,
        reviewsCount: 0,
        activitiesCount: 0
      };
    }
  }

  // ============================================================================
  // RECENT MOVIES MANAGEMENT
  // ============================================================================

  // saveRecentMovie - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async saveRecentMovie(user: User, movieData: TMDbMovie): Promise<void> {
    return await dataService.saveRecentMovie(user, movieData);
  }

  // getRecentMovies - REMOVED: Now available in shared/dataService.ts (~25 lines removed)
  // Temporary delegation method for backward compatibility
  async getRecentMovies(user: User): Promise<TMDbMovie[]> {
    return await dataService.getRecentMovies(user);
  }

  // clearRecentMovies - REMOVED: Now available in shared/dataService.ts
  // Temporary delegation method for backward compatibility
  async clearRecentMovies(user: User): Promise<void> {
    return await dataService.clearRecentMovies(user);
  }

  // Backup recent movies to localStorage for offline access
  async syncRecentMoviesToLocal(user: User): Promise<void> {
    try {
      const recentMovies = await this.getRecentMovies(user);
      localStorage.setItem('recentMovies', JSON.stringify(recentMovies));
      console.log('Firebase: Synced recent movies to localStorage:', recentMovies.length);
    } catch (error) {
      console.error('Error syncing recent movies to localStorage:', error);
    }
  }

  // Load recent movies from localStorage and sync to Firebase
  async loadRecentMoviesFromLocal(user: User): Promise<void> {
    try {
      const localRecentMovies = localStorage.getItem('recentMovies');
      if (!localRecentMovies) return;

      const movies: TMDbMovie[] = JSON.parse(localRecentMovies);

      // Save each movie to Firebase (they will be deduplicated automatically)
      for (const movie of movies) {
        await this.saveRecentMovie(user, movie);
      }

      console.log('Firebase: Loaded recent movies from localStorage to Firebase:', movies.length);
    } catch (error) {
      console.error('Error loading recent movies from localStorage:', error);
    }
  }

  // ============================================================================
  // RECENTLY ADDED MOVIES MANAGEMENT
  // ============================================================================

  // saveRecentlyAddedMovies - REMOVED: Now available in shared/dataService.ts (~28 lines removed)
  // Temporary delegation method for backward compatibility
  async saveRecentlyAddedMovies(user: User, movies: TMDbMovie[]): Promise<void> {
    return await dataService.saveRecentlyAddedMovies(user, movies);
  }

  // getRecentlyAddedMovies - REMOVED: Now available in shared/dataService.ts (~27 lines removed)
  // Temporary delegation method for backward compatibility
  async getRecentlyAddedMovies(user: User): Promise<TMDbMovie[]> {
    return await dataService.getRecentlyAddedMovies(user);
  }

  // shouldRefreshRecentlyAddedMovies - REMOVED: Now available in shared/dataService.ts (~16 lines removed)
  // Temporary delegation method for backward compatibility
  async shouldRefreshRecentlyAddedMovies(user: User): Promise<boolean> {
    return await dataService.shouldRefreshRecentlyAddedMovies(user);
  }

  /**
   * Save user feedback to Firebase and Google Sheets
   */
  async saveFeedback(uid: string, feedbackData: {
    feedback: string;
    timestamp: Date;
    userEmail: string;
    userName: string;
    appVersion: string;
  }): Promise<void> {
    try {
      // Save to Firebase first
      await unifiedAddDoc('feedback', {
        uid,
        feedback: feedbackData.feedback,
        timestamp: feedbackData.timestamp.toISOString(),
        userEmail: feedbackData.userEmail,
        userName: feedbackData.userName,
        appVersion: feedbackData.appVersion,
        status: 'new',
        createdAt: new Date().toISOString(),
      });

      console.log('Feedback saved to Firebase successfully');

      // Also send to Google Sheets (non-blocking)
      try {
        const { googleSheetsService } = await import('./googleSheetsService');
        await googleSheetsService.sendFeedbackToSheets({
          uid,
          feedback: feedbackData.feedback,
          timestamp: feedbackData.timestamp,
          userEmail: feedbackData.userEmail,
          userName: feedbackData.userName,
          appVersion: feedbackData.appVersion,
          status: 'new',
        });
        console.log('Feedback also sent to Google Sheets');
      } catch (sheetsError) {
        console.warn('Failed to send feedback to Google Sheets (non-critical):', sheetsError);
        // Don't throw error here - Google Sheets is secondary storage
      }

    } catch (error) {
      console.error('Error saving feedback:', error);
      throw error;
    }
  }

  // Sync watchlist data to ensure consistency between movies array and movieDetails array
  async syncWatchlistMovieData(watchlistId: string): Promise<void> {
    try {
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        console.warn('Watchlist not found for sync:', watchlistId);
        return;
      }

      const watchlistData = watchlistDoc.data();
      const movies = watchlistData.movies || [];
      const movieDetails = watchlistData.movieDetails || [];

      console.log('FirebaseService: Syncing watchlist data:', {
        watchlistId,
        moviesCount: movies.length,
        movieDetailsCount: movieDetails.length
      });

      // Create sets for efficient lookups
      const movieIds = new Set(movies);
      const movieDetailIds = new Set(movieDetails.map((movie: any) => movie.id || movie.imdbID));

      // Find movies that are in movies array but not in movieDetails
      const missingFromDetails = movies.filter((movieId: string) => !movieDetailIds.has(movieId));

      // Find movies that are in movieDetails but not in movies array
      const missingFromMovies = movieDetails
        .map((movie: any) => movie.id || movie.imdbID)
        .filter((movieId: string) => !movieIds.has(movieId));

      console.log('FirebaseService: Sync analysis:', {
        missingFromDetails: missingFromDetails.length,
        missingFromMovies: missingFromMovies.length
      });

      let needsUpdate = false;
      const updates: any = {};

      // Add missing movie details (fetch from TMDB if needed)
      if (missingFromDetails.length > 0) {
        console.log('FirebaseService: Fetching missing movie details for:', missingFromDetails);
        try {
          const { tmdbService } = await import('../services/tmdbApi');
          const { getMovieDetails: functionsGetMovieDetails, shouldUseFunctionsAPI } = await import('../services/tmdbFunctionsApi');

          const newMovieDetails = await Promise.all(
            missingFromDetails.map(async (movieId: string) => {
              try {
                let movieData: any;

                // Use Functions API if available, fallback to direct TMDB
                if (shouldUseFunctionsAPI()) {
                  try {
                    const functionsMovie = await functionsGetMovieDetails(movieId);
                    movieData = {
                      Response: 'True',
                      Title: functionsMovie.title,
                      Year: functionsMovie.release_date ? functionsMovie.release_date.split('-')[0] : '',
                      Poster: functionsMovie.poster_path ? `https://image.tmdb.org/t/p/w500${functionsMovie.poster_path}` : 'N/A',
                      Genre: functionsMovie.genres?.map(g => g.name).join(', ') || '',
                      Director: functionsMovie.credits?.crew?.find(c => c.job === 'Director')?.name || '',
                      Plot: functionsMovie.overview || '',
                      imdbRating: functionsMovie.vote_average?.toFixed(1) || ''
                    };
                  } catch (functionsError) {
                    console.warn('Functions getMovieDetails failed, using rate-limited TMDB:', functionsError);
                    console.log('🎬 FirebaseService: Using rate-limited queue for movie', movieId);
                    movieData = await tmdbRequestQueue.getMovieData(movieId);
                  }
                } else {
                  console.log('🎬 FirebaseService: Using rate-limited queue for movie', movieId);
                  movieData = await tmdbRequestQueue.getMovieData(movieId);
                }
                if (movieData.Response === "True") {
                  return {
                    id: movieId,
                    title: movieData.Title,
                    year: movieData.Year,
                    poster: movieData.Poster !== 'N/A' ? movieData.Poster : undefined,
                    imdbID: movieId,
                    genre: movieData.Genre,
                    director: movieData.Director,
                    plot: movieData.Plot,
                    rating: movieData.imdbRating,
                    addedAt: new Date().toISOString()
                  };
                } else {
                  return {
                    id: movieId,
                    title: `Unknown Movie (${movieId})`,
                    imdbID: movieId,
                    addedAt: new Date().toISOString()
                  };
                }
              } catch (error) {
                console.warn('Failed to fetch movie details for:', movieId, error);
                return {
                  id: movieId,
                  title: `Unknown Movie (${movieId})`,
                  imdbID: movieId,
                  addedAt: new Date().toISOString()
                };
              }
            })
          );

          updates.movieDetails = [...movieDetails, ...newMovieDetails];
          needsUpdate = true;
        } catch (error) {
          console.error('Failed to fetch missing movie details:', error);
        }
      }

      // Add missing movie IDs
      if (missingFromMovies.length > 0) {
        console.log('FirebaseService: Adding missing movie IDs:', missingFromMovies);
        updates.movies = [...movies, ...missingFromMovies];
        needsUpdate = true;
      }

      // Apply updates if needed
      if (needsUpdate) {
        updates.updatedAt = serverTimestamp();
        await unifiedUpdateDoc('watchlists', watchlistId, updates);
        console.log('FirebaseService: Watchlist sync completed successfully');
      } else {
        console.log('FirebaseService: Watchlist already in sync');
      }

    } catch (error) {
      console.error('FirebaseService: Error syncing watchlist data:', error);
      throw error;
    }
  }

  // ===== OTHER USERS' DATA METHODS =====

  // Get movie data for another user (respects privacy)
  async getOtherUserMovieData(
    userId: string,
    movieId: string
  ): Promise<UserMovieData | null> {
    try {
      // Get user profile to check privacy settings
      const userProfile = await getCachedUserProfile(userId);
      if (!userProfile) {
        return null;
      }

      const docSnap = await unifiedGetDoc('userMovies', `${userId}_${movieId}`);

      if (docSnap.exists()) {
        const data = docSnap.data() as UserMovieData;

        // For public profiles: respect individual privacy settings
        if (userProfile.privacy.profileVisibility === 'public') {
          if (userProfile.privacy.showRatings || userProfile.privacy.showWatchlist) {
            return data;
          }
        }

        // For private profiles: Still allow access to basic profile info and stats
        // (per requirements: private users appear in search, profile info accessible)
        // But don't show detailed movie data/ratings unless explicitly allowed
        if (userProfile.privacy.profileVisibility === 'private') {
          // Only return basic watchlist status, not detailed ratings/reviews
          return {
            ...data,
            rating: undefined,
            review: undefined,
            notes: undefined,
            tags: undefined
          };
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting other user movie data:', error);
      return null;
    }
  }

  // Get all watchlist movies for another user (public only)
  async getOtherUserWatchlistMovies(userId: string): Promise<string[]> {
    try {
      // Check privacy settings first
      const userProfile = await getCachedUserProfile(userId);
      if (!userProfile ||
        userProfile.privacy.profileVisibility === 'private' ||
        !userProfile.privacy.showWatchlist) {
        return [];
      }

      const docs = await unifiedCollection('userMovies')
        .where('uid', '==', userId)
        .where('isInWatchlist', '==', true)
        .getDocs();
      return docs.map(d => d.data().movieId);
    } catch (error) {
      console.error('Error getting other user watchlist movies:', error);
      return [];
    }
  }

  // Get all watched movies for another user (public only)
  async getOtherUserWatchedMovies(userId: string): Promise<UserMovieData[]> {
    try {
      // Check privacy settings first
      const userProfile = await getCachedUserProfile(userId);
      if (!userProfile ||
        userProfile.privacy.profileVisibility === 'private' ||
        !userProfile.privacy.showRatings) {
        return [];
      }

      const docs = await unifiedCollection('userMovies')
        .where('uid', '==', userId)
        .where('isWatched', '==', true)
        .getDocs();
      const watchedMovies = docs.map(d => d.data() as UserMovieData);

      // Filter out private reviews if user doesn't allow showing ratings
      return watchedMovies.map(movie => ({
        ...movie,
        review: userProfile.privacy.showRatings ? movie.review : undefined,
        rating: userProfile.privacy.showRatings ? movie.rating : undefined
      }));
    } catch (error) {
      console.error('Error getting other user watched movies:', error);
      return [];
    }
  }

  // Get ALL movie data for another user (comprehensive method, respects privacy)
  async getOtherUserAllMovieData(userId: string): Promise<UserMovieData[]> {
    try {
      console.log('FirebaseService: Fetching other user movie data for user:', userId);

      // Check privacy settings first
      const userProfile = await getCachedUserProfile(userId);
      if (!userProfile || userProfile.privacy.profileVisibility === 'private') {
        console.log('FirebaseService: User profile is private, returning empty data');
        return [];
      }

      const rawDocs = await unifiedCollection('userMovies').where('uid', '==', userId).getDocs();
      const allMovieData = rawDocs.map(d => {
        const data = d.data() as UserMovieData;

        // Respect privacy settings
        const filteredData: UserMovieData = {
          ...data,
          // Only include watchlist data if user allows it
          isInWatchlist: userProfile.privacy.showWatchlist ? data.isInWatchlist : false,
          // Only include ratings/reviews if user allows it
          rating: userProfile.privacy.showRatings ? data.rating : undefined,
          review: userProfile.privacy.showRatings ? data.review : undefined,
          isWatched: userProfile.privacy.showRatings ? data.isWatched : false,
          watchedDate: userProfile.privacy.showRatings ? data.watchedDate : undefined,
        };

        return filteredData;
      });

      console.log('FirebaseService: Retrieved', allMovieData.length, 'movie records for other user');
      console.log('FirebaseService: Privacy settings applied:', {
        showWatchlist: userProfile.privacy.showWatchlist,
        showRatings: userProfile.privacy.showRatings,
        profileVisibility: userProfile.privacy.profileVisibility
      });

      return allMovieData;
    } catch (error) {
      console.error('Error getting other user all movie data:', error);
      return [];
    }
  }

  // Get movies from a specific watchlist for another user (respects privacy and watchlist settings)
  async getOtherUserWatchlistData(userId: string, watchlistId: string, currentUserUid?: string): Promise<{
    watchlist: UserWatchlist | null;
    movieData: UserMovieData[];
  }> {
    try {
      console.log('FirebaseService: Fetching watchlist data for user:', userId, 'watchlist:', watchlistId, 'currentUser:', currentUserUid);

      // Get the watchlist first
      const watchlistSnap = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistSnap.exists()) {
        console.log('FirebaseService: Watchlist not found');
        return { watchlist: null, movieData: [] };
      }

      const watchlist = { id: watchlistSnap.id, ...watchlistSnap.data() } as UserWatchlist;

      // 🚀 CRITICAL FIX: Check if watchlist is accessible by the profile user
      // For PUBLIC watchlists: Allow access to anyone
      // For PRIVATE watchlists: Only allow access if profile user is owner or collaborator
      const isOwnedByProfileUser = watchlist.createdBy === userId;
      const isProfileUserCollaborator = watchlist.collaborators && watchlist.collaborators.includes(userId);

      if (watchlist.isPrivate && !isOwnedByProfileUser && !isProfileUserCollaborator) {
        console.log('FirebaseService: Private watchlist not accessible - profile user is neither owner nor collaborator');
        return { watchlist: null, movieData: [] };
      }

      if (!watchlist.isPrivate) {
        console.log('FirebaseService: Public watchlist access granted to any user');
      }

      console.log('FirebaseService: Watchlist access granted for profile user:', {
        isOwned: isOwnedByProfileUser,
        isCollaborator: isProfileUserCollaborator
      });

      // Access control already handled above - no need for redundant checks

      // Get user's privacy settings (only for non-collaborative access)
      if (!currentUserUid || (watchlist.createdBy !== currentUserUid && !watchlist.collaborators?.includes(currentUserUid))) {
        const userProfile = await getCachedUserProfile(userId);
        if (!userProfile || userProfile.privacy.profileVisibility === 'private') {
          console.log('FirebaseService: User profile is private');
          return { watchlist: null, movieData: [] };
        }
      }

      // Standardize movie data format for consistency (same as UserWatchlistDataContext)
      const standardizedMovieDetails = watchlist.movieDetails
        .filter((movie: any) => {
          const isValid = movie && (movie.id || movie.imdbID) && (movie.title || movie.Title);
          if (!isValid) {
            console.warn('FirebaseService: Filtering out invalid movie from other user watchlist:', movie);
          }
          return isValid;
        })
        .map((movie: any) => {
          // Standardize movie data format to ensure consistent field names
          return {
            ...movie,
            // Ensure consistent field names for UI compatibility
            Title: movie.Title || movie.title || 'Unknown Movie',
            title: movie.title || movie.Title || 'Unknown Movie',
            Year: movie.Year || movie.year || movie.release_date?.split('-')[0] || 'Unknown',
            year: movie.year || movie.Year || movie.release_date?.split('-')[0] || 'Unknown',
            Poster: movie.Poster || (movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null),
            poster_path: movie.poster_path || (movie.Poster?.includes('tmdb.org') ? movie.Poster.split('/').pop() : null),
            imdbID: movie.imdbID || movie.imdb_id || `tmdb_${movie.id}`,
            id: movie.id || movie.imdbID?.replace('tmdb_', '') || 'unknown'
          };
        });

      // Update watchlist with standardized movie details
      const standardizedWatchlist = {
        ...watchlist,
        movieDetails: standardizedMovieDetails
      };

      // Get movie data for movies in the watchlist
      const movieIds = standardizedMovieDetails
        .map(movie => movie.id || movie.imdbID)
        .filter((id): id is string => Boolean(id));
      const movieDataPromises = movieIds.map(movieId => this.getOtherUserMovieData(userId, movieId));
      const movieDataResults = await Promise.all(movieDataPromises);
      const movieData = movieDataResults.filter(data => data !== null) as UserMovieData[];

      console.log('FirebaseService: Retrieved watchlist with', movieData.length, 'standardized movies');

      return { watchlist: standardizedWatchlist, movieData };
    } catch (error) {
      console.error('Error getting other user watchlist data:', error);
      return { watchlist: null, movieData: [] };
    }
  }

  // Clean up old/duplicate likes - removes likes that don't follow the unique constraint
  async cleanupOldLikes(): Promise<void> {
    try {
      console.log('Starting cleanup of old/duplicate likes...');

      // Get all likes
      const likesRef = collection(getDb(), 'watchlistLikes');
      const allLikesSnapshot = await getDocs(likesRef);

      // Group likes by userUid_watchlistId pattern
      const likeGroups = new Map<string, any[]>();
      const validLikes = new Set<string>();

      allLikesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const expectedId = `${data.userUid}_${data.watchlistId}`;

        if (doc.id === expectedId) {
          // This is a properly formatted like
          validLikes.add(doc.id);
        } else {
          // This is an old/malformed like
          const group = likeGroups.get(expectedId) || [];
          group.push({ id: doc.id, data });
          likeGroups.set(expectedId, group);
        }
      });

      // Remove old/duplicate likes
      let deletedCount = 0;
      for (const [expectedId, duplicates] of Array.from(likeGroups.entries())) {
        if (validLikes.has(expectedId)) {
          // We have a valid like, remove all duplicates
          for (const duplicate of duplicates) {
            await unifiedDeleteDoc('watchlistLikes', duplicate.id);
            deletedCount++;
            console.log(`Deleted duplicate like: ${duplicate.id}`);
          }
        } else if (duplicates.length > 1) {
          // No valid like exists, keep the first one and delete the rest
          const [keep, ...toDelete] = duplicates;

          // Create the properly formatted like
          await unifiedSetDoc('watchlistLikes', expectedId, keep.data);

          // Delete all old versions including the one we kept
          for (const duplicate of duplicates) {
            await unifiedDeleteDoc('watchlistLikes', duplicate.id);
            deletedCount++;
            console.log(`Deleted old like: ${duplicate.id}`);
          }

          console.log(`Created properly formatted like: ${expectedId}`);
        } else if (duplicates.length === 1) {
          // Single old like, recreate it with proper format
          const oldLike = duplicates[0];
          await unifiedSetDoc('watchlistLikes', expectedId, oldLike.data);
          await unifiedDeleteDoc('watchlistLikes', oldLike.id);
          deletedCount++;
          console.log(`Reformatted like: ${oldLike.id} -> ${expectedId}`);
        }
      }

      console.log(`Cleanup completed. Deleted/reformatted ${deletedCount} likes.`);
    } catch (error) {
      console.error('Error cleaning up old likes:', error);
      throw error;
    }
  }

  // Enhanced movie fetching for collages that prefers backdrop images
  async getMovieWithBackdrop(movieId: string): Promise<{ poster: string; backdrop: string | null; title: string } | null> {
    try {
      // Import tmdbService here to avoid circular dependency
      const { tmdbService } = await import('../services/tmdbApi');
      const { getMovieDetails: functionsGetMovieDetails, shouldUseFunctionsAPI } = await import('../services/tmdbFunctionsApi');

      let movie: any;

      // Use Functions API if available, fallback to direct TMDB
      if (shouldUseFunctionsAPI()) {
        try {
          const functionsMovie = await functionsGetMovieDetails(movieId);
          movie = {
            Response: 'True',
            Title: functionsMovie.title,
            poster_path: functionsMovie.poster_path,
            backdrop_path: functionsMovie.backdrop_path
          };
        } catch (functionsError) {
          console.warn('Functions getMovieDetails failed, using rate-limited TMDB:', functionsError);
          console.log('🎬 FirebaseService: Using rate-limited queue for backdrop movie', movieId);
          movie = await tmdbRequestQueue.getMovieData(movieId);
        }
      } else {
        console.log('🎬 FirebaseService: Using rate-limited queue for backdrop movie', movieId);
        movie = await tmdbRequestQueue.getMovieData(movieId);
      }
      if (movie.Response !== "True") return null;

      // Try to get backdrop from TMDb if we have the poster
      let backdrop: string | null = null;
      if (movie.poster_path || movie.backdrop_path) {
        backdrop = movie.backdrop_path ?
          `https://image.tmdb.org/t/p/w1280${movie.backdrop_path}` :
          null;
      }

      return {
        poster: movie.Poster && movie.Poster !== 'N/A' ? movie.Poster : '',
        backdrop,
        title: movie.Title || 'Unknown Movie'
      };
    } catch (error) {
      console.warn(`Failed to fetch movie with backdrop ${movieId}:`, error);
      return null;
    }
  }



  // ========================================
  // COLLABORATION METHODS
  // ========================================

  /**
   * Add a collaborator to a watchlist
   */
  async addCollaboratorToWatchlist(watchlistId: string, collaboratorUid: string, inviterUid: string): Promise<void> {
    try {
      console.log('FirebaseService: Adding collaborator to watchlist:', { watchlistId, collaboratorUid, inviterUid });

      // Get the watchlist first to check permissions and get current collaborators
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const watchlistData = watchlistDoc.data() as UserWatchlist;

      // Check if inviter has permission (must be owner or existing collaborator)
      if (watchlistData.createdBy !== inviterUid &&
        !(watchlistData.collaborators || []).includes(inviterUid)) {
        throw new Error('Permission denied: You cannot add collaborators to this watchlist');
      }

      // Check if user is already a collaborator
      const currentCollaborators = watchlistData.collaborators || [];
      if (currentCollaborators.includes(collaboratorUid)) {
        throw new Error('User is already a collaborator on this watchlist');
      }

      // Check if user is the owner
      if (watchlistData.createdBy === collaboratorUid) {
        throw new Error('Cannot add the owner as a collaborator');
      }

      // Get collaborator's profile for name
      const collaboratorProfile = await getCachedUserProfile(collaboratorUid);
      if (!collaboratorProfile) {
        throw new Error('Collaborator user profile not found');
      }

      // Add collaborator to the watchlist
      const updatedCollaborators = [...currentCollaborators, collaboratorUid];

      // IMPORTANT: When adding first collaborator to a private list, automatically enable collaboration
      // Private lists can ONLY be shared through collaboration, so this should be enabled by default
      const updateData: any = {
        collaborators: updatedCollaborators,
        lastModifiedBy: inviterUid,
        updatedAt: serverTimestamp()
      };

      // If this is the first collaborator being added to a private list, 
      // the watchlist becomes collaborative-private (isPrivate stays true but now has collaborators)
      if (currentCollaborators.length === 0 && watchlistData.isPrivate) {
        console.log('FirebaseService: Enabling collaboration for private list - first collaborator added');
        // No need to change isPrivate - it stays true but now supports collaboration
        // The privacy type will be computed as 'collaborative-private' by the UI
      }

      await unifiedUpdateDoc('watchlists', watchlistId, updateData);

      // Create collaboration invitation notification for the new collaborator
      const watchlistPosters = watchlistData.movieDetails?.slice(0, 4)
        .map((m: any) => m.poster)
        .filter((poster: any): poster is string => !!poster) || [];

      await this.createCollaborationInvitationNotification(
        inviterUid,
        collaboratorUid,
        watchlistId,
        watchlistData.name,
        watchlistData.movieDetails?.length || 0,
        watchlistPosters
      );

      // Create activity for the inviter
      await this.addCollaboratorAddedActivity(
        { uid: inviterUid } as User,
        watchlistId,
        watchlistData.name,
        collaboratorUid,
        collaboratorProfile.displayName,
        collaboratorProfile.photoURL
      );

      console.log('FirebaseService: Collaborator added successfully');
    } catch (error) {
      console.error('FirebaseService: Error adding collaborator:', error);
      throw error;
    }
  }

  /**
   * Remove a collaborator from a watchlist
   */
  async removeCollaboratorFromWatchlist(watchlistId: string, collaboratorUid: string, removerUid: string): Promise<void> {
    try {
      console.log('FirebaseService: Removing collaborator:', { watchlistId, collaboratorUid, removerUid });

      // Get the watchlist first to check current state
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const watchlistData = watchlistDoc.data() as UserWatchlist;
      console.log('FirebaseService: Current watchlist collaborators:', watchlistData.collaborators);

      // Check if remover has permission (owner or the collaborator removing themselves)
      if (watchlistData.createdBy !== removerUid &&
        collaboratorUid !== removerUid &&
        !(watchlistData.collaborators || []).includes(removerUid)) {
        throw new Error('Permission denied: You cannot remove collaborators from this watchlist');
      }

      // Check if user is actually a collaborator
      const currentCollaborators = watchlistData.collaborators || [];
      if (!currentCollaborators.includes(collaboratorUid)) {
        // If user is trying to remove themselves but they're not in the list, just return success
        if (collaboratorUid === removerUid) {
          console.log('FirebaseService: User is already not a collaborator, treating as success');
          return;
        }
        console.warn('FirebaseService: User is not found in collaborators list:', {
          collaboratorUid,
          currentCollaborators,
          watchlistId
        });
        throw new Error('User is not a collaborator on this watchlist');
      }

      // Remove collaborator from the watchlist
      const updatedCollaborators = currentCollaborators.filter(uid => uid !== collaboratorUid);
      const prevAdmins = watchlistData.adminCollaboratorIds || [];
      const adminCollaboratorIds = prevAdmins.filter(uid => uid !== collaboratorUid);
      console.log('FirebaseService: Updating collaborators from', currentCollaborators, 'to', updatedCollaborators);

      await unifiedUpdateDoc('watchlists', watchlistId, {
        collaborators: updatedCollaborators,
        adminCollaboratorIds,
        lastModifiedBy: removerUid,
        updatedAt: serverTimestamp()
      });

      // Create notification for the removed collaborator (if they didn't remove themselves)
      if (collaboratorUid !== removerUid) {
        try {
          await this.createNotification({
            recipientId: collaboratorUid,
            type: 'collaboration',
            actorId: removerUid,
            actorName: (await getCachedUserProfile(removerUid))?.displayName || 'Someone',
            entityId: watchlistId,
            entityName: watchlistData.name,
            message: `You've been removed from the watchlist "${watchlistData.name}"`,
            metadata: {
              watchlistName: watchlistData.name,
              action: 'removed'
            }
          });
        } catch (notificationError) {
          console.warn('FirebaseService: Failed to create notification for removed collaborator:', notificationError);
          // Don't fail the whole operation if notification fails
        }
      }

      console.log('FirebaseService: Collaborator removed successfully');
    } catch (error) {
      console.error('FirebaseService: Error removing collaborator:', error);
      throw error;
    }
  }

  /**
   * Check if a user can edit a watchlist (owner or collaborator)
   */
  async canUserEditWatchlist(watchlistId: string, userUid: string): Promise<boolean> {
    try {
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        return false;
      }

      const watchlistData = watchlistDoc.data() as UserWatchlist;

      // User can edit if they are the owner or a collaborator
      return watchlistData.createdBy === userUid ||
        (watchlistData.collaborators || []).includes(userUid);
    } catch (error) {
      console.error('FirebaseService: Error checking edit permissions:', error);
      return false;
    }
  }

  /**
   * Get all watchlists where the user is a collaborator
   */
  async getCollaborativeWatchlists(userUid: string): Promise<UserWatchlist[]> {
    try {
      const docs = await unifiedCollection('watchlists')
        .where('collaborators', 'array-contains', userUid)
        .getDocs();
      const watchlists = docs.map(d => ({ id: d.id, ...d.data() })) as UserWatchlist[];

      console.log(`FirebaseService: Found ${watchlists.length} collaborative watchlists for user ${userUid}`);
      return watchlists;
    } catch (error) {
      console.error('FirebaseService: Error getting collaborative watchlists:', error);
      return [];
    }
  }

  /**
   * Get collaborator details for a watchlist
   */
  async getWatchlistCollaborators(watchlistId: string): Promise<UserProfile[]> {
    try {
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        return [];
      }

      const watchlistData = watchlistDoc.data() as UserWatchlist;
      const collaboratorUids = watchlistData.collaborators || [];

      if (collaboratorUids.length === 0) {
        return [];
      }

      // Get collaborator profiles
      const collaboratorProfiles = await Promise.all(
        collaboratorUids.map(async (uid) => {
          try {
            return await getCachedUserProfile(uid);
          } catch (error) {
            console.warn(`Failed to get profile for collaborator ${uid}:`, error);
            return null;
          }
        })
      );

      // Filter out null results
      return collaboratorProfiles.filter(profile => profile !== null) as UserProfile[];
    } catch (error) {
      console.error('FirebaseService: Error getting watchlist collaborators:', error);
      return [];
    }
  }

  /**
   * Change a collaborator's role in a watchlist
   */
  async changeCollaboratorRole(watchlistId: string, collaboratorUid: string, newRole: 'admin' | 'editor', changerUid: string): Promise<void> {
    try {
      console.log('FirebaseService: Changing collaborator role:', { watchlistId, collaboratorUid, newRole, changerUid });

      // Get the watchlist first to check permissions
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const watchlistData = watchlistDoc.data() as UserWatchlist;

      // Check if changer has permission (must be owner)
      if (watchlistData.createdBy !== changerUid) {
        throw new Error('Permission denied: Only the watchlist owner can change collaborator roles');
      }

      // Check if user is actually a collaborator
      const currentCollaborators = watchlistData.collaborators || [];
      if (!currentCollaborators.includes(collaboratorUid)) {
        throw new Error('User is not a collaborator on this watchlist');
      }

      const prevAdmins = watchlistData.adminCollaboratorIds || [];
      const adminSet = new Set(prevAdmins);
      if (newRole === 'admin') {
        adminSet.add(collaboratorUid);
      } else {
        adminSet.delete(collaboratorUid);
      }
      const adminCollaboratorIds = Array.from(adminSet);

      const updatePayload: Record<string, unknown> = {
        adminCollaboratorIds,
        lastModifiedBy: changerUid,
        updatedAt: serverTimestamp()
      };

      const storedProfiles = (watchlistData as UserWatchlist & { collaboratorProfiles?: Array<{ uid?: string } & Record<string, unknown>> })
        .collaboratorProfiles;
      if (Array.isArray(storedProfiles) && storedProfiles.length > 0) {
        updatePayload.collaboratorProfiles = storedProfiles.map(profile =>
          profile.uid === collaboratorUid ? { ...profile, role: newRole } : profile
        );
      }

      await unifiedUpdateDoc('watchlists', watchlistId, updatePayload);

      // Create notification for the collaborator about role change
      try {
        const collaboratorProfile = await getCachedUserProfile(collaboratorUid);
        if (collaboratorProfile) {
          await this.createNotification({
            recipientId: collaboratorUid,
            type: 'collaboration',
            actorId: changerUid,
            actorName: (await getCachedUserProfile(changerUid))?.displayName || 'Someone',
            entityId: watchlistId,
            entityName: watchlistData.name,
            message: `Your role in "${watchlistData.name}" has been changed to ${newRole}`,
            metadata: {
              watchlistName: watchlistData.name,
              action: 'role_changed',
              newRole
            }
          });
        }
      } catch (notificationError) {
        console.warn('FirebaseService: Failed to create role change notification:', notificationError);
        // Don't fail the whole operation if notification fails
      }

      console.log('FirebaseService: Collaborator role changed successfully');
    } catch (error) {
      console.error('FirebaseService: Error changing collaborator role:', error);
      throw error;
    }
  }

  /**
   * Transfer ownership of a watchlist to another user
   */
  async transferWatchlistOwnership(watchlistId: string, newOwnerUid: string, currentOwnerUid: string): Promise<void> {
    try {
      console.log('FirebaseService: Transferring watchlist ownership:', { watchlistId, newOwnerUid, currentOwnerUid });

      // Get the watchlist first to check permissions
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const watchlistData = watchlistDoc.data() as UserWatchlist;

      // Check if current user is the owner
      if (watchlistData.createdBy !== currentOwnerUid) {
        throw new Error('Permission denied: Only the watchlist owner can transfer ownership');
      }

      // Check if new owner is actually a collaborator
      const currentCollaborators = watchlistData.collaborators || [];
      if (!currentCollaborators.includes(newOwnerUid)) {
        throw new Error('New owner must be an existing collaborator');
      }

      // Get new owner profile for notifications
      const newOwnerProfile = await getCachedUserProfile(newOwnerUid);
      if (!newOwnerProfile) {
        throw new Error('New owner profile not found');
      }

      // Remove new owner from collaborators list and update owner
      const updatedCollaborators = currentCollaborators.filter(uid => uid !== newOwnerUid);
      // Add the previous owner as a collaborator
      updatedCollaborators.push(currentOwnerUid);

      const prevAdmins = watchlistData.adminCollaboratorIds || [];
      const adminCollaboratorIds = prevAdmins.filter(uid => uid !== newOwnerUid);

      // Update the watchlist
      const updateData: any = {
        createdBy: newOwnerUid,
        collaborators: updatedCollaborators,
        adminCollaboratorIds,
        updatedAt: serverTimestamp()
      };

      // Only add name/avatar if they exist to avoid null values
      if (newOwnerProfile.displayName) {
        updateData.createdByName = newOwnerProfile.displayName;
      }
      if (newOwnerProfile.photoURL) {
        updateData.createdByAvatar = newOwnerProfile.photoURL;
      }

      await unifiedUpdateDoc('watchlists', watchlistId, updateData);

      // Create notification for the new owner
      await this.createNotification({
        recipientId: newOwnerUid,
        type: 'collaboration',
        actorId: currentOwnerUid,
        actorName: (await getCachedUserProfile(currentOwnerUid))?.displayName || 'Someone',
        entityId: watchlistId,
        entityName: watchlistData.name,
        message: `You are now the owner of "${watchlistData.name}"`,
        metadata: {
          watchlistName: watchlistData.name,
          action: 'ownership_transferred'
        }
      });

      // Create notification for all other collaborators
      const allCollaborators = updatedCollaborators.filter(uid => uid !== currentOwnerUid);
      for (const collaboratorUid of allCollaborators) {
        await this.createNotification({
          recipientId: collaboratorUid,
          type: 'collaboration',
          actorId: currentOwnerUid,
          actorName: (await getCachedUserProfile(currentOwnerUid))?.displayName || 'Someone',
          entityId: watchlistId,
          entityName: watchlistData.name,
          message: `Ownership of "${watchlistData.name}" has been transferred to ${newOwnerProfile.displayName}`,
          metadata: {
            watchlistName: watchlistData.name,
            action: 'ownership_transferred',
            newOwnerName: newOwnerProfile.displayName
          }
        });
      }

      console.log('FirebaseService: Watchlist ownership transferred successfully');
    } catch (error) {
      console.error('FirebaseService: Error transferring watchlist ownership:', error);
      throw error;
    }
  }

  /**
   * Enhanced user search with username support
   */
  async searchUsersForCollaboration(searchTerm: string, limitCount: number = 10): Promise<UserProfile[]> {
    try {
      const docs = await unifiedCollection('userProfiles')
        .where('privacy.profileVisibility', '==', 'public')
        .getDocs();
      const users = docs.map(d => d.data() as UserProfile);

      // Client-side filtering for better search results
      const searchTermLower = searchTerm.toLowerCase();
      const filteredUsers = users.filter(user => {
        const displayNameMatch = user.displayName?.toLowerCase().includes(searchTermLower);
        const emailMatch = user.email?.toLowerCase().includes(searchTermLower);
        const usernameMatch = user.username?.toLowerCase().includes(searchTermLower);

        return displayNameMatch || emailMatch || usernameMatch;
      });

      // Sort by relevance (exact username match first, then display name, then email)
      filteredUsers.sort((a, b) => {
        const aUsernameExact = a.username?.toLowerCase() === searchTermLower;
        const bUsernameExact = b.username?.toLowerCase() === searchTermLower;

        if (aUsernameExact && !bUsernameExact) return -1;
        if (bUsernameExact && !aUsernameExact) return 1;

        const aDisplayNameExact = a.displayName?.toLowerCase() === searchTermLower;
        const bDisplayNameExact = b.displayName?.toLowerCase() === searchTermLower;

        if (aDisplayNameExact && !bDisplayNameExact) return -1;
        if (bDisplayNameExact && !aDisplayNameExact) return 1;

        return 0;
      });

      return filteredUsers.slice(0, limitCount);
    } catch (error) {
      console.error('FirebaseService: Error searching users for collaboration:', error);
      return [];
    }
  }

  /**
   * Accept a collaboration invitation
   */
  async acceptCollaborationInvitation(notificationId: string, watchlistId: string, userUid: string): Promise<void> {
    try {
      console.log('FirebaseService: Accepting collaboration invitation:', { notificationId, watchlistId, userUid });

      // Get the watchlist to check if it exists and user isn't already a collaborator
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const watchlistData = watchlistDoc.data() as UserWatchlist;
      const currentCollaborators = watchlistData.collaborators || [];

      // Check if user is already a collaborator
      if (currentCollaborators.includes(userUid)) {
        throw new Error('User is already a collaborator on this watchlist');
      }

      // Add user to collaborators
      const updatedCollaborators = [...currentCollaborators, userUid];

      await unifiedUpdateDoc('watchlists', watchlistId, {
        collaborators: updatedCollaborators,
        lastModifiedBy: userUid,
        updatedAt: serverTimestamp()
      });

      // Mark the notification as read and accepted
      const notificationRef = doc(getDb(), 'notifications', notificationId);
      await unifiedUpdateDoc('notifications', notificationId, {
        read: true,
        readAt: serverTimestamp(),
        accepted: true,
        acceptedAt: serverTimestamp(),
        // Change the message to show it was accepted
        message: `You accepted the collaboration invitation for "${watchlistData.name}"`,
        type: 'collaborator_added' // Change type to remove buttons
      });

      // Create a confirmation notification for the watchlist owner
      const watchlistPosters = watchlistData.movieDetails?.slice(0, 4)
        .map((m: any) => m.poster)
        .filter((poster: any): poster is string => !!poster) || [];

      await this.createCollaboratorAddedNotification(
        watchlistData.createdBy,
        userUid,
        watchlistId,
        watchlistData.name,
        watchlistData.movieDetails?.length,
        watchlistPosters
      );

      console.log('FirebaseService: Collaboration invitation accepted successfully');
    } catch (error) {
      console.error('FirebaseService: Error accepting collaboration invitation:', error);
      throw error;
    }
  }

  /**
   * Decline a collaboration invitation
   */
  async declineCollaborationInvitation(notificationId: string, watchlistId: string, userUid: string): Promise<void> {
    try {
      console.log('FirebaseService: Declining collaboration invitation:', { notificationId, watchlistId, userUid });

      // Mark the notification as read and declined
      const notificationRef = doc(getDb(), 'notifications', notificationId);
      await unifiedUpdateDoc('notifications', notificationId, {
        read: true,
        readAt: serverTimestamp(),
        declined: true,
        declinedAt: serverTimestamp()
      });

      // Optionally, create a notification for the inviter (commented out to avoid spam)
      /*
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);
      
      if (watchlistDoc.exists()) {
        const watchlistData = watchlistDoc.data() as UserWatchlist;
        const declinerProfile = await getCachedUserProfile(userUid);
        
        if (declinerProfile) {
          await this.createNotification({
            recipientId: watchlistData.createdBy,
            type: 'collaboration',
            actorId: userUid,
            actorName: declinerProfile.displayName,
            actorAvatar: declinerProfile.photoURL,
            entityId: watchlistId,
            entityName: watchlistData.name,
            message: `${declinerProfile.displayName} declined your collaboration invitation for "${watchlistData.name}"`,
            metadata: {
              watchlistName: watchlistData.name,
              action: 'declined'
            }
          });
        }
      }
      */

      console.log('FirebaseService: Collaboration invitation declined successfully');
    } catch (error) {
      console.error('FirebaseService: Error declining collaboration invitation:', error);
      throw error;
    }
  }

  // Fix follower/following count mismatches
  async cleanupFollowerCounts(userId?: string): Promise<void> {
    try {
      console.log('🧹 Starting follower count cleanup...');

      // If specific user ID provided, cleanup for that user only
      if (userId) {
        await this.recalculateUserStats(userId);
        return;
      }

      // Otherwise, cleanup for all users (admin function)
      const usersQuery = query(collection(getDb(), 'userProfiles'));
      const usersSnapshot = await getDocs(usersQuery);

      const batchSize = 20;
      let processed = 0;

      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        await this.recalculateUserStats(userId);
        processed++;

        if (processed % batchSize === 0) {
          console.log(`✅ Processed ${processed}/${usersSnapshot.docs.length} users`);
          // Small delay to avoid rate limits
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      console.log('✅ Follower count cleanup completed');
    } catch (error) {
      console.error('❌ Follower count cleanup failed:', error);
      throw error;
    }
  }

  // Recalculate stats for a specific user
  async recalculateUserStats(userId: string): Promise<void> {
    try {
      const [followers, following, userWatchlists] = await Promise.all([
        this.getFollowers(userId),
        this.getFollowing(userId),
        watchlistService.getUserWatchlists(userId)
      ]);

      // Calculate likes received across all user's watchlists
      let likesReceived = 0;
      for (const watchlist of userWatchlists) {
        const likes = await this.getWatchlistLikers(watchlist.id);
        likesReceived += likes.length;
      }

      // Update user profile stats
      const userRef = doc(getDb(), 'userProfiles', userId);
      await unifiedUpdateDoc('userProfiles', userId, {
        'stats.followersCount': followers.length,
        'stats.followingCount': following.length,
        'stats.likesReceived': likesReceived,
        'stats.listsCreated': userWatchlists.length,
        updatedAt: serverTimestamp()
      });

      console.log(`✅ Updated stats for user ${userId}:`, {
        followers: followers.length,
        following: following.length,
        likesReceived,
        listsCreated: userWatchlists.length
      });
    } catch (error) {
      console.error(`❌ Failed to recalculate stats for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Debug utility: Get current watchlist data from Firestore to compare with UI data
   */
  async debugWatchlistData(watchlistId: string): Promise<{
    exists: boolean;
    data?: UserWatchlist;
    collaborators?: string[];
    owner?: string;
  }> {
    try {
      console.log('FirebaseService: Debug - fetching watchlist data for:', watchlistId);

      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        console.log('FirebaseService: Debug - watchlist does not exist');
        return { exists: false };
      }

      const data = watchlistDoc.data() as UserWatchlist;
      console.log('FirebaseService: Debug - watchlist data:', {
        id: watchlistId,
        name: data.name,
        createdBy: data.createdBy,
        collaborators: data.collaborators || [],
        movieCount: data.movieDetails?.length || 0
      });

      return {
        exists: true,
        data,
        collaborators: data.collaborators || [],
        owner: data.createdBy
      };
    } catch (error) {
      console.error('FirebaseService: Debug - error fetching watchlist:', error);
      return { exists: false };
    }
  }

  // ✅ COMPREHENSIVE LIKE COUNT SYNC - Fixes mismatch between followerCount and actual likes
  async syncAllWatchlistLikeCounts(): Promise<{
    totalFixed: number;
    watchlistsChecked: number;
    mismatches: Array<{
      watchlistId: string;
      name: string;
      beforeCount: number;
      afterCount: number;
    }>;
  }> {
    console.log('🔧 Starting comprehensive like count sync for all watchlists...');

    try {
      // Get all watchlists
      const watchlistsSnapshot = await getDocs(collection(getDb(), 'watchlists'));
      const mismatches: Array<{
        watchlistId: string;
        name: string;
        beforeCount: number;
        afterCount: number;
      }> = [];

      let totalFixed = 0;

      for (const watchlistDoc of watchlistsSnapshot.docs) {
        const watchlistData = watchlistDoc.data() as UserWatchlist;
        const watchlistId = watchlistDoc.id;
        const currentCount = watchlistData.followerCount || 0;

        // Count actual likes in watchlistLikes collection
        const actualLikes = await this.getWatchlistLikers(watchlistId);
        const actualCount = actualLikes.length;

        if (currentCount !== actualCount) {
          console.log(`📊 Like count mismatch for "${watchlistData.name}":`, {
            stored: currentCount,
            actual: actualCount,
            difference: actualCount - currentCount
          });

          // Update the watchlist with the correct count
          await unifiedUpdateDoc('watchlists', watchlistId, {
            followerCount: actualCount,
            updatedAt: serverTimestamp()
          });

          mismatches.push({
            watchlistId,
            name: watchlistData.name,
            beforeCount: currentCount,
            afterCount: actualCount
          });

          totalFixed++;
        }
      }

      console.log(`✅ Like count sync completed:`, {
        watchlistsChecked: watchlistsSnapshot.docs.length,
        totalFixed,
        mismatches: mismatches.length
      });

      return {
        totalFixed,
        watchlistsChecked: watchlistsSnapshot.docs.length,
        mismatches
      };

    } catch (error) {
      console.error('❌ Error syncing like counts:', error);
      throw error;
    }
  }

  // Helper function to get real-time accurate like count for any watchlist
  async getAccurateLikeCount(watchlistId: string): Promise<number> {
    try {
      const actualLikes = await this.getWatchlistLikers(watchlistId);
      return actualLikes.length;
    } catch (error) {
      console.error('Error getting accurate like count:', error);
      return 0;
    }
  }

  // 🚧 TEMPORARY DELEGATION METHODS - TO BE REMOVED AFTER MIGRATION
  // These methods delegate to shared services to maintain backward compatibility
  // while we complete the architecture consolidation

  // getUserProfile - REMOVED: Now available in shared/authService.ts

  // getUserProfileLite - REMOVED: Now available in shared/authService.ts
}

export const firebaseService = new FirebaseService();
export default firebaseService;

// 🔧 DEBUG: Add sync function to global window for browser console access
if (typeof window !== 'undefined') {
  (window as any).fixWatchlistLikeCount = async (watchlistId: string) => {
    try {
      const result = await firebaseService.syncWatchlistLikeCount(watchlistId);
      console.log('🔧 Sync result:', result);
      return result;
    } catch (error) {
      console.error('❌ Sync failed:', error);
      throw error;
    }
  };

  (window as any).fixAllWatchlistLikeCounts = async () => {
    try {
      console.log('🔧 Starting to fix all watchlist like counts...');

      // Get all watchlists
      const watchlistsQuery = query(collection(getDb(), 'watchlists'));
      const watchlistsSnapshot = await getDocs(watchlistsQuery);

      console.log(`📊 Found ${watchlistsSnapshot.size} watchlists to check`);

      const results: any[] = [];
      let fixedCount = 0;

      for (const watchlistDoc of watchlistsSnapshot.docs) {
        const watchlistId = watchlistDoc.id;
        const result = await firebaseService.syncWatchlistLikeCount(watchlistId);

        results.push({ watchlistId, ...result });

        if (result.fixed) {
          fixedCount++;
        }

        // Small delay to avoid overwhelming Firestore
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log(`🎉 Sync complete! Fixed ${fixedCount} out of ${watchlistsSnapshot.size} watchlists`);
      return results;

    } catch (error) {
      console.error('❌ Bulk sync failed:', error);
      throw error;
    }
  };
} 